import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './PhoneScreens.css'

/* Sheets need to anchor to the phone bezel, NOT scroll with content.
   We render them via a portal into a .sol-phone-overlays sibling of
   .sol-phone-content so position: absolute resolves to the bezel
   viewport, not 'wherever the user happened to scroll'. */
const OverlayContext = createContext<HTMLElement | null>(null)
function SheetPortal({ children }: { children: React.ReactNode }) {
  const host = useContext(OverlayContext)
  if (!host) return <>{children}</>
  return createPortal(children, host)
}

type ActiveTab = 'home' | 'contracts' | 'payments' | 'more'

/* App-wide theme = palette/styling, applied to the whole .sol-phone-screen
   so backgrounds, tabbar, and sheets all participate. Four directions:
     classic    — paper neutral
     chartreuse — bold brand wash
     aurora     — soft pastel gradient
     dark       — night palette from DESIGN.md (stone-warm Linear ladder)
   Persisted to sessionStorage so a tab switch + return keeps the pick. */
type PhoneTheme = 'classic' | 'chartreuse' | 'aurora' | 'dark'
const PHONE_THEMES: { id: PhoneTheme; label: string; sub: string }[] = [
  { id: 'classic',    label: 'Paper',      sub: 'Calm neutral'   },
  { id: 'chartreuse', label: 'Chartreuse', sub: 'Brand wash'     },
  { id: 'aurora',     label: 'Aurora',     sub: 'Soft gradient'  },
  { id: 'dark',       label: 'Dark',       sub: 'Night palette'  },
]

/* Wallpaper = a personal photo behind the Home greeting + wallet, like
   an iOS Home Screen wallpaper. Independent of theme — picking a
   wallpaper does not change the rest of the app's palette. Lives only
   on Home; other tabs (Contracts/Payments/More) follow the theme alone. */
type PhoneWallpaper = 'none' | 'orchard'
const PHONE_WALLPAPERS: { id: PhoneWallpaper; label: string; sub: string }[] = [
  { id: 'none',    label: 'None',    sub: 'Theme only'      },
  { id: 'orchard', label: 'Orchard', sub: 'Apple blossom'   },
]

const PhoneThemeContext = createContext<{
  theme: PhoneTheme
  setTheme: (t: PhoneTheme) => void
  wallpaper: PhoneWallpaper
  setWallpaper: (w: PhoneWallpaper) => void
  glassBlur: number       /* px — backdrop blur on the hero glass panel */
  setGlassBlur: (n: number) => void
  heroRadius: number      /* px — corner radius on Home hero card */
  setHeroRadius: (n: number) => void
  wallpaperBlur: number   /* px — gaussian blur on the photo itself */
  setWallpaperBlur: (n: number) => void
}>({
  theme: 'classic',
  setTheme: () => {},
  wallpaper: 'orchard',
  setWallpaper: () => {},
  glassBlur: 18,
  setGlassBlur: () => {},
  heroRadius: 22,
  setHeroRadius: () => {},
  wallpaperBlur: 0,
  setWallpaperBlur: () => {},
})

/* Full-screen success takeover — mirrors PhoneMockup's
   .sol-phone-sign-screen: chartreuse background slides up from the
   bottom, cream-white disc scales in at center, brand-ink check draws
   across with stroke-dashoffset. Re-used everywhere a swipe-to-confirm
   completes (sign a contract, send a payment, request paid). Renders
   as an absolutely positioned sibling so it covers the entire phone
   screen including status bar + tabbar — just like the mockup. */
function SuccessScreen({ label }: { label?: string }) {
  return (
    <div className="phs-success-screen" aria-hidden>
      <div className="phs-success-disk">
        <svg viewBox="0 0 32 32" className="phs-success-check" aria-hidden>
          <path
            d="M 7.5 17 L 13.5 23 L 24.5 10.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
          />
        </svg>
      </div>
      {label && <div className="phs-success-label">{label}</div>}
    </div>
  )
}

/* Shared close hook for bottom-sheets — drives a `closing` flag so the
   sheet plays a slide-down animation before unmounting. The CSS owns
   the actual translation; React just toggles the class. */
function useSheetClose(onClose: () => void, durationMs = 300) {
  const [closing, setClosing] = useState(false)
  const requestClose = () => {
    if (closing) return
    setClosing(true)
    window.setTimeout(onClose, durationMs)
  }
  return { closing, requestClose }
}

/* Vertical drag handler for bottom sheets.
   - Drag DOWN past 30% of sheet height → requestClose (slide-down anim)
   - Drag UP past 60px AND onExpand is provided → onExpand (e.g. half →
     full sheet detent on ApproveSheet)
   - Anything short of threshold → snap back to rest position
   Drag origin is restricted to the top 64px (grip + close-button band)
   so internal sliders / list rows / scrollable text don't trigger it.
   Visual follow-finger only on downward drags; upward drags are
   detected and committed on release without a translate, so the panel
   doesn't poke above the bezel mid-drag. */
function useSheetSwipe(
  ref: React.RefObject<HTMLDivElement | null>,
  requestClose: () => void,
  onExpand?: () => void,
) {
  const startY = useRef<number | null>(null)
  const dragging = useRef(false)
  const dy = useRef(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return
      const rect = el.getBoundingClientRect()
      if (e.clientY - rect.top > 64) return /* outside grip band */
      startY.current = e.clientY
      dragging.current = false
      dy.current = 0
    }
    const onMove = (e: PointerEvent) => {
      if (startY.current == null) return
      const diff = e.clientY - startY.current
      if (Math.abs(diff) < 4) return
      if (!dragging.current) {
        dragging.current = true
        el.classList.add('phs-tx-sheet--dragging')
        try { el.setPointerCapture(e.pointerId) } catch (_) { /* noop */ }
      }
      dy.current = diff
      /* Translate the panel only when pulling DOWN — upward drag is
         purely a gesture detect; we don't want the panel to clip
         above the bezel during drag. */
      el.style.transform = diff > 0 ? `translateY(${diff}px)` : ''
    }
    const onUp = () => {
      if (!dragging.current) {
        startY.current = null
        return
      }
      const h = el.offsetHeight
      const passedDown = dy.current > Math.min(140, h * 0.3)
      const passedUp = onExpand && dy.current < -60
      el.style.transform = ''
      el.classList.remove('phs-tx-sheet--dragging')
      if (passedDown) {
        requestClose()
      } else if (passedUp) {
        onExpand!()
      } else {
        el.classList.add('phs-tx-sheet--snapback')
        window.setTimeout(() => el.classList.remove('phs-tx-sheet--snapback'), 320)
      }
      startY.current = null
      dragging.current = false
      dy.current = 0
    }
    el.addEventListener('pointerdown', onDown)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      el.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [ref, requestClose])
}

/* iPhone 13 mini reference: 375×812pt logical (Apple verified specs).
   At a 268px bezel width the screen height is 268 × (812/375) ≈ 580px
   which matches the device aspect (2.165) precisely. */

/* ── Inline SVGs ───────────────────────────────────────────────────── */
function Chevron() {
  return (
    <svg className="phs-chevron" viewBox="0 0 8 14" fill="none" aria-hidden>
      <path d="M 1.5 1 L 6.5 7 L 1.5 13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function CheckMini() {
  return (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M 2.5 6.2 L 5 8.5 L 9.5 3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function ArrowRight() {
  return (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M 3 7 H 11 M 7.5 3.5 L 11 7 L 7.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/* Quick-action chip icons (compact 14px SVGs for the chip strip). */
function SparkleMini() {
  return (
    <svg viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <path d="M 7 1.4 L 8 5 L 11.6 6 L 8 7 L 7 10.6 L 6 7 L 2.4 6 L 6 5 z" />
      <path d="M 11 9 L 11.5 10.5 L 13 11 L 11.5 11.5 L 11 13 L 10.5 11.5 L 9 11 L 10.5 10.5 z" />
    </svg>
  )
}
function StackMini() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" aria-hidden>
      <rect x="3.2" y="4.2" width="6.6" height="7.8" rx="1.2" />
      <path d="M 5.2 4.2 V 3 a 1 1 0 0 1 1 -1 H 10.8 a 1 1 0 0 1 1 1 v 7.4" />
    </svg>
  )
}
function DownloadMini() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 7 2 v 7 M 4 6 l 3 3 l 3 -3" />
      <path d="M 3 12 h 8" />
    </svg>
  )
}
function ScanMini() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 2 4.5 V 3 a 1 1 0 0 1 1 -1 H 4.5" />
      <path d="M 9.5 2 H 11 a 1 1 0 0 1 1 1 V 4.5" />
      <path d="M 12 9.5 V 11 a 1 1 0 0 1 -1 1 H 9.5" />
      <path d="M 4.5 12 H 3 a 1 1 0 0 1 -1 -1 V 9.5" />
      <path d="M 4 7 h 6" />
    </svg>
  )
}
function ArrowUpRight() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 4 10 L 10 4 M 5 4 H 10 V 9" />
    </svg>
  )
}
function ArrowDownLeft() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 10 4 L 4 10 M 9 10 H 4 V 5" />
    </svg>
  )
}
function SwapMini() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 2 5 H 10 M 8 3 L 10 5 L 8 7" />
      <path d="M 12 9 H 4 M 6 7 L 4 9 L 6 11" />
    </svg>
  )
}

/* ── Tab bar icons (proper SVG, not unicode glyphs) ────────────────── */
function TabIconHome() {
  return (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M 2.5 8 L 9 2.6 L 15.5 8 v 7 a 0.5 0.5 0 0 1 -0.5 0.5 H 3 a 0.5 0.5 0 0 1 -0.5 -0.5 z" />
      <path d="M 7 15.5 v -4 h 4 v 4" />
    </svg>
  )
}
function TabIconContracts() {
  return (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M 3 2.5 h 9 l 3 3 v 10 H 3 z" />
      <path d="M 12 2.5 v 3 h 3" />
      <path d="M 5.5 9 h 7 M 5.5 12 h 5" />
    </svg>
  )
}
function TabIconPayments() {
  return (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M 2.5 6 H 14 M 11.5 3 L 14.5 6 L 11.5 9" />
      <path d="M 15.5 12 H 4 M 6.5 9 L 3.5 12 L 6.5 15" />
    </svg>
  )
}
function TabIconMore() {
  return (
    <svg viewBox="0 0 18 18" fill="currentColor">
      <circle cx="4" cy="9" r="1.4" />
      <circle cx="9" cy="9" r="1.4" />
      <circle cx="14" cy="9" r="1.4" />
    </svg>
  )
}

/* ── Settings list icons ───────────────────────────────────────────── */
function IconUser() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <circle cx="8" cy="6" r="2.8" />
      <path d="M 2.4 13.5 c 0.6 -2.6 3 -4 5.6 -4 s 5 1.4 5.6 4 c -2 0.6 -3.8 0.8 -5.6 0.8 s -3.6 -0.2 -5.6 -0.8 z" />
    </svg>
  )
}
function IconDollar() {
  return (
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M 8 2 v 12 M 11 4.8 c -0.4 -1.2 -1.8 -1.8 -3 -1.8 s -2.6 0.7 -2.6 2.2 c 0 1.3 1.4 1.8 2.6 2.2 s 2.6 0.7 2.6 2.2 s -1.2 2.2 -2.6 2.2 s -2.6 -0.6 -3 -1.8"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function IconGlobe() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="8" r="6" />
      <path d="M 2 8 h 12" />
      <path d="M 8 2 c 1.8 2.2 1.8 9.8 0 12 c -1.8 -2.2 -1.8 -9.8 0 -12 z" />
    </svg>
  )
}
function IconKey() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <circle cx="5.5" cy="10.5" r="2.6" />
      <path d="M 7.5 8.7 l 5.5 -5.5 l 1.5 1.5 l -1.5 1.5 l 1.2 1.2 l -1.5 1.5 l -1.2 -1.2" />
    </svg>
  )
}
function IconFingerprint() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <path d="M 3.5 11.5 c 0 -4 2 -7 4.5 -7 s 4.5 3 4.5 7" />
      <path d="M 5.8 12.8 c 0 -3 1 -5.5 2.2 -5.5 s 2.2 2.5 2.2 5.5" />
      <path d="M 8 13.6 v -4" />
    </svg>
  )
}
function IconBell() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M 3 11.6 c 0.8 -0.4 1.2 -1.6 1.2 -3 v -1 c 0 -2.2 1.6 -3.9 3.8 -3.9 s 3.8 1.7 3.8 3.9 v 1 c 0 1.4 0.4 2.6 1.2 3 v 0.6 H 3 v -0.6 z" />
      <path d="M 6.5 13.5 c 0.3 0.7 0.9 1 1.5 1 s 1.2 -0.3 1.5 -1 z" />
    </svg>
  )
}

/* ── PhoneFrame (chrome only — bezel, status bar, tab bar) ─────────── */
function PhoneFrame({
  active,
  onTabChange,
  phoneTheme,
  wallpaper,
  wallpaperBlur,
  children,
}: {
  active: ActiveTab
  onTabChange: (t: ActiveTab) => void
  phoneTheme?: PhoneTheme
  wallpaper?: PhoneWallpaper
  wallpaperBlur?: number
  children: React.ReactNode
}) {
  const [overlayHost, setOverlayHost] = useState<HTMLDivElement | null>(null)
  return (
    <div className="sol-vis sol-vis-phone phs-interactive">
      <div className="sol-phone sol-phone--wide">
        <div className="sol-phone-bezel sol-phone-bezel--mock">
          <div
            className="sol-phone-screen"
            data-phone-theme={phoneTheme}
            data-phone-wallpaper={wallpaper}
            data-active-tab={active}
            style={{ ['--phs-wallpaper-blur' as string]: `${wallpaperBlur ?? 0}px` }}
          >
            {/* Wallpaper backdrop — painted at the screen level (above
                .sol-phone-content, behind the status bar visually) so
                the photo bleeds under the iOS status bar like a real
                Home Screen wallpaper. Visible only on Home; CSS gates
                via data-active-tab so other tabs read on plain
                surface. */}
            <div className="phs-screen-wallpaper" aria-hidden />
            <div className="sol-phone-statusbar" aria-hidden>
              <span className="sol-phone-time">9:41</span>
              <div className="sol-phone-island" />
              <span className="sol-phone-icons">
                <span className="sol-phone-signal"><i /><i /><i /><i /></span>
                <span className="sol-phone-wifi" />
                <span className="sol-phone-battery"><span /></span>
              </span>
            </div>

            {/* Switch screens with a fade+slight-rise — key on tab so React
                remounts content and the CSS animation replays. */}
            <div className="sol-phone-content phs-content" key={active}>
              <OverlayContext.Provider value={overlayHost}>
                {children}
              </OverlayContext.Provider>
            </div>

            {/* Overlay host — sibling of scrolling content so portaled
                sheets anchor to the phone bezel, not the scroll. */}
            <div className="sol-phone-overlays" ref={setOverlayHost} />

            <div className="phs-tabbar">
              <button
                type="button"
                className={`phs-tab${active === 'home' ? ' phs-tab--active' : ''}`}
                onClick={() => onTabChange('home')}
                aria-label="Home"
              >
                <TabIconHome />
                <span>Home</span>
              </button>
              <button
                type="button"
                className={`phs-tab${active === 'contracts' ? ' phs-tab--active' : ''}`}
                onClick={() => onTabChange('contracts')}
                aria-label="Contracts"
              >
                <TabIconContracts />
                <span>Contracts</span>
              </button>
              <button
                type="button"
                className={`phs-tab${active === 'payments' ? ' phs-tab--active' : ''}`}
                onClick={() => onTabChange('payments')}
                aria-label="Payments"
              >
                <TabIconPayments />
                <span>Payments</span>
              </button>
              <button
                type="button"
                className={`phs-tab${active === 'more' ? ' phs-tab--active' : ''}`}
                onClick={() => onTabChange('more')}
                aria-label="More"
              >
                <TabIconMore />
                <span>More</span>
              </button>
            </div>

            <div className="sol-phone-home" aria-hidden />
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Screens ───────────────────────────────────────────────────────── */

/* ── Agent activity icons ──────────────────────────────────────────── */
function ActionCheck() {
  return (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M 3 7.2 L 5.8 9.8 L 11 4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function ActionFlag() {
  /* Half-filled circle — communicates "needs review" without an alarmist
     amber-warning glyph. Reads as a ring with an arc fill at our size. */
  return (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M 7 2.5 A 4.5 4.5 0 0 1 7 11.5 Z" fill="currentColor" />
    </svg>
  )
}
function AgentBolt() {
  return (
    <svg viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <path d="M 8 1.5 L 3 8 H 6.5 L 5.5 12.5 L 11 5.5 H 7.5 z" />
    </svg>
  )
}

/* AgentRunCard — compact feed of "what the agent just did" with results
   inline. Acts as the Home tab's anchor card so the user immediately sees
   value before scrolling. Three action rows with chartreuse checks for
   resolved items and an outline circle for the one item that needs human
   review. Mirrors the DagPipeline narrative (intake → match → review) but
   collapsed to a one-screen feed suitable for an app's home tab. */
function AgentRunCard() {
  return (
    <div className="phs-agent-run">
      <div className="phs-agent-run-head">
        <span className="phs-agent-run-badge"><AgentBolt /></span>
        <div className="phs-agent-run-headtxt">
          <span className="phs-agent-run-label">Agent run</span>
          <span className="phs-agent-run-meta">2 min ago · 3 actions</span>
        </div>
        <button type="button" className="phs-agent-run-rerun">Run again</button>
      </div>
      <div className="phs-agent-run-actions">
        <div className="phs-agent-run-action phs-row--stagger" style={{ animationDelay: '40ms' }}>
          <span className="phs-agent-run-action-icon phs-agent-run-action-icon--done"><ActionCheck /></span>
          <div className="phs-agent-run-action-body">
            <span className="phs-agent-run-action-title">Reconciled INV-2491 → Stripe payout</span>
            <span className="phs-agent-run-action-result">+€ 1 240 · auto-matched single candidate</span>
          </div>
        </div>
        <div className="phs-agent-run-action phs-row--stagger" style={{ animationDelay: '100ms' }}>
          <span className="phs-agent-run-action-icon phs-agent-run-action-icon--done"><ActionCheck /></span>
          <div className="phs-agent-run-action-body">
            <span className="phs-agent-run-action-title">Matched Claude · monthly</span>
            <span className="phs-agent-run-action-result">−€ 20 · recurring rule applied</span>
          </div>
        </div>
        <div className="phs-agent-run-action phs-row--stagger" style={{ animationDelay: '160ms' }}>
          <span className="phs-agent-run-action-icon phs-agent-run-action-icon--flag"><ActionFlag /></span>
          <div className="phs-agent-run-action-body">
            <span className="phs-agent-run-action-title">Flagged Notion charge</span>
            <span className="phs-agent-run-action-result">−€ 15 · 1 candidate, needs review</span>
          </div>
        </div>
      </div>
      <button type="button" className="phs-agent-run-foot">
        View all 3 actions <ArrowRight />
      </button>
    </div>
  )
}

/* PhoneSceneReconciled — compact phone-fit version of MarketingScenes'
   SceneCashReconciled. Where the marketing card stacks BANK/INV legs
   horizontally with a center "bond", here we stack vertically so labels
   fit at 240 px. Bank row → chartreuse "+" bond → invoice row. */
function PhoneSceneReconciled() {
  return (
    <div className="phs-mini-recon">
      <div className="phs-mini-head">
        <span className="phs-mini-head-eyebrow">Reconciled this morning</span>
        <span className="phs-mini-head-stat">247</span>
      </div>
      <div className="phs-mini-recon-pair">
        <div className="phs-mini-recon-leg">
          <span className="phs-mini-recon-tag">BANK</span>
          <span className="phs-mini-recon-label">Studio Aria d.o.o.</span>
          <span className="phs-mini-recon-amount phs-mini-recon-amount--in">+124 850</span>
        </div>
        <div className="phs-mini-recon-bond" aria-hidden>
          <span className="phs-mini-recon-bond-line" />
          <span className="phs-mini-recon-bond-glyph"><ActionCheck /></span>
          <span className="phs-mini-recon-bond-line" />
        </div>
        <div className="phs-mini-recon-leg">
          <span className="phs-mini-recon-tag">INV</span>
          <span className="phs-mini-recon-label">INV-2026-091</span>
          <span className="phs-mini-recon-amount">124 850</span>
        </div>
      </div>
      <div className="phs-mini-recon-pair phs-mini-recon-pair--pending">
        <div className="phs-mini-recon-leg">
          <span className="phs-mini-recon-tag">BANK</span>
          <span className="phs-mini-recon-label">Atelje Nikolić</span>
          <span className="phs-mini-recon-amount phs-mini-recon-amount--in">+64 000</span>
        </div>
        <div className="phs-mini-recon-bond" aria-hidden>
          <span className="phs-mini-recon-bond-line" />
          <span className="phs-mini-recon-bond-dots"><span /><span /><span /></span>
          <span className="phs-mini-recon-bond-line" />
        </div>
        <div className="phs-mini-recon-leg">
          <span className="phs-mini-recon-tag">INV</span>
          <span className="phs-mini-recon-label phs-mini-recon-label--muted">matching…</span>
          <span className="phs-mini-recon-amount phs-mini-recon-amount--muted">—</span>
        </div>
      </div>
    </div>
  )
}

/* PhoneSceneAging — overdue receivables mini-scene: 4-tick rail (0d/7d/
   14d/30d) with 3 pins, plus one explicit overdue row. Communicates
   "agent is watching aging" without taking the full marketing footprint. */
function PhoneSceneAging() {
  return (
    <div className="phs-mini-aging">
      <div className="phs-mini-head">
        <span className="phs-mini-head-eyebrow">Receivables · aging</span>
        <span className="phs-mini-head-stat phs-mini-head-stat--warn">3 overdue</span>
      </div>
      <div className="phs-mini-aging-rail" aria-hidden>
        <span className="phs-mini-aging-tick" style={{ left: '0%' }} />
        <span className="phs-mini-aging-tick" style={{ left: '33%' }} />
        <span className="phs-mini-aging-tick" style={{ left: '66%' }} />
        <span className="phs-mini-aging-tick phs-mini-aging-tick--warn" style={{ left: '100%' }} />
        <span className="phs-mini-aging-pin" style={{ left: '18%' }}>
          <span className="phs-mini-aging-pin-dot" />
          <span className="phs-mini-aging-pin-label">3d</span>
        </span>
        <span className="phs-mini-aging-pin" style={{ left: '46%' }}>
          <span className="phs-mini-aging-pin-dot" />
          <span className="phs-mini-aging-pin-label">7d</span>
        </span>
        <span className="phs-mini-aging-pin phs-mini-aging-pin--warn" style={{ left: '82%' }}>
          <span className="phs-mini-aging-pin-dot" />
          <span className="phs-mini-aging-pin-label">12d</span>
        </span>
      </div>
      <div className="phs-mini-aging-row">
        <span className="phs-row-logo phs-row-logo--mono">AN</span>
        <div className="phs-row-body">
          <div className="phs-row-line1">
            <span className="phs-row-title">Atelje Nikolić</span>
            <span className="phs-row-amount">64 000</span>
          </div>
          <div className="phs-row-substatus phs-row-substatus--done">
            <ActionCheck /> Reminder sent · 12d overdue
          </div>
        </div>
      </div>
    </div>
  )
}

type AccountKey = 'eur' | 'usd' | 'rsd'
type FlagKey = 'eu' | 'us' | 'rs'
type AccountData = {
  flag: FlagKey
  code: string
  sub: string
  amount: string
  iban: string
  inflow: string
  outflow: string
}
const HOME_ACCOUNTS: Record<AccountKey, AccountData> = {
  eur: { flag: 'eu', code: 'EUR', sub: 'Operating', amount: '€4 826.40', iban: 'RS35 — 6660 ···· 0014', inflow: '+€ 1 985 (7d)', outflow: '−€ 412 (7d)' },
  usd: { flag: 'us', code: 'USD', sub: 'Payouts',   amount: '$1 240.00', iban: 'US — •••• 2811',         inflow: '+$ 1 240 (7d)', outflow: '$ 0 (7d)' },
  rsd: { flag: 'rs', code: 'RSD', sub: 'Fiscal',    amount: '580 200',   iban: 'RS35 — 6660 ···· 0099', inflow: '+820 000 (7d)', outflow: '−239 800 (7d)' },
}

const HOME_RECENT: TxData[] = [
  { merchant: 'Stripe payout', logo: '/logo-stripe.png', amount: '+€ 1 240', direction: 'in',  matched: true, invoice: 'INV-2491', category: 'Stripe payout', time: 'Today · 09:41', account: 'EUR · Operating' },
  { merchant: 'Claude',       logo: '/logo-claude.png', amount: '−€ 20',   direction: 'out', matched: true, invoice: 'AI · monthly', category: 'Software',     time: 'Today · 06:00', account: 'EUR · Operating' },
]

/// Returns a time-of-day greeting prefix for the Home hero. Cheap
/// "lived in" texture — the demo reads differently at 9am vs midnight.
/// Buckets: 5-11 morning, 12-17 afternoon, 18-22 evening, 23-4 late.
function greetingForNow(): string {
  const h = new Date().getHours()
  if (h >= 5 && h < 12) return 'Good morning'
  if (h >= 12 && h < 18) return 'Good afternoon'
  if (h >= 18 && h < 23) return 'Good evening'
  return 'Working late'
}

function HomeScreen() {
  const [selectedAccount, setSelectedAccount] = useState<AccountKey | null>(null)
  const [selectedTx, setSelectedTx] = useState<TxData | null>(null)
  const [openInvoice, setOpenInvoice] = useState<InvoiceData | null>(null)
  const [profileOpen, setProfileOpen] = useState(false)
  const [appearanceOpen, setAppearanceOpen] = useState(false)
  const { glassBlur, heroRadius } = useContext(PhoneThemeContext)
  return (
    <>
      <div
        className="phs-home-hero"
        style={{
          ['--phs-glass-blur' as string]: `${glassBlur}px`,
          ['--phs-hero-radius' as string]: `${heroRadius}px`,
        }}
      >
        <div className="phs-greeting">
          <div className="phs-greeting-body">
            <span className="phs-greeting-hi">{greetingForNow()}, Marko</span>
            <span className="phs-greeting-sub">acc.tixo · Operating €</span>
          </div>
          <button
            type="button"
            className="phs-greeting-avatar phs-greeting-avatar--button"
            onClick={() => setProfileOpen(true)}
            aria-label="Open profile"
          >
            <img src="/marko.jpg" alt="" />
          </button>
        </div>

        {/* Wallet — mirrors PhoneMockup's .sol-phone-wallet-accounts 1:1
            so the marketing parallax card and the in-app Home tab share
            one accounts visual. Active row uses subtle surface-hover, not
            chartreuse-soft, to match the PhoneMockup screenshot. */}
        <div className="sol-phone-wallet">
          <div className="sol-phone-wallet-accounts">
            {(Object.keys(HOME_ACCOUNTS) as AccountKey[]).map((key, i) => {
              const a = HOME_ACCOUNTS[key]
              return (
                <button
                  key={key}
                  type="button"
                  className={`sol-phone-wallet-account phs-wallet-button${i === 0 ? ' sol-phone-wallet-account--main' : ''}`}
                  onClick={() => setSelectedAccount(key)}
                >
                  <span className={`sol-phone-wallet-flag sol-phone-wallet-flag--${a.flag}`} aria-hidden />
                  <div className="sol-phone-wallet-info">
                    <span className="sol-phone-wallet-name">{a.code}</span>
                    <span className="sol-phone-wallet-sub">{a.sub}</span>
                  </div>
                  <span className="sol-phone-wallet-amt">{a.amount}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="phs-today">
        <span className="phs-today-label">Today</span>
        <span className="phs-today-status">
          <span className="phs-today-dot" />
          Auto-balanced
        </span>
      </div>

      <span className="phs-section">
        Recent
        <span className="phs-section-link">See all</span>
      </span>
      <div className="phs-list">
        {HOME_RECENT.map((tx, i) => (
          <button
            key={tx.merchant}
            type="button"
            className="phs-row phs-row--button phs-row--stagger"
            style={{ animationDelay: `${40 + i * 80}ms` }}
            onClick={() => setSelectedTx(tx)}
          >
            <span className="phs-row-logo">
              {tx.logo ? <img src={tx.logo} alt="" /> : <span>{tx.initials}</span>}
            </span>
            <div className="phs-row-body">
              <div className="phs-row-line1">
                <span className="phs-row-title">{tx.merchant}</span>
                <span className={`phs-row-amount${tx.direction === 'in' ? ' phs-row-amount--in' : ''}`}>{tx.amount}</span>
              </div>
              <div className="phs-row-line2">
                <span className="phs-row-sub">{tx.category} · {tx.time.replace('Today · ', '')}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="phs-customize-row"
        onClick={() => setAppearanceOpen(true)}
      >
        <span className="phs-customize-row-icon"><IconPalette /></span>
        <span className="phs-customize-row-label">Customize Home</span>
        <Chevron />
      </button>

      {selectedAccount && <AccountDetailSheet account={HOME_ACCOUNTS[selectedAccount]} onClose={() => setSelectedAccount(null)} />}
      {selectedTx && (
        <TxDetailSheet
          tx={selectedTx}
          onClose={() => setSelectedTx(null)}
          onOpenInvoice={(ref) => { const inv = INVOICES[ref]; if (inv) setOpenInvoice(inv) }}
        />
      )}
      {openInvoice && <InvoiceDetailSheet invoice={openInvoice} onClose={() => setOpenInvoice(null)} />}
      {profileOpen && <ProfileSheet onClose={() => setProfileOpen(false)} />}
      {appearanceOpen && <AppearanceSheet onClose={() => setAppearanceOpen(false)} />}
    </>
  )
}

/* ── Ask-agent sheet — opens from the Contracts "Ask agent" tile.
   User picks a contract type from the prompt chips, agent appears to
   type out a draft block-by-block (chartreuse caret blink + line-reveal
   animation), then a "Open draft" CTA. Models the real flow without
   needing an LLM round-trip. */
const ASK_AGENT_PROMPTS = [
  'Service agreement with Studio Aria · €4 800',
  'NDA with Atelje Nikolić',
  'Freelance contract · React work · €60/h',
  'Office sublease · 6 months',
]
const AGENT_DRAFT_LINES = [
  'PARTIES — acc.tixo OÜ and Studio Aria d.o.o.',
  'SCOPE — design + frontend, 6 calendar weeks, May 12 → Jun 23.',
  'FEE — €4 800 (net), invoiced on milestone delivery.',
  'IP — work product transfers on final payment.',
  'TERM — 30-day notice for early termination.',
]
function AskAgentSheet({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<'pick' | 'thinking' | 'drafting' | 'done'>('pick')
  const [reveal, setReveal] = useState(0)
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)

  const handlePick = (i: number) => {
    if (phase !== 'pick') return
    setPhase('thinking')
    setTimeout(() => setPhase('drafting'), 700)
    /* Reveal lines one at a time with a small stagger so it reads as
       the agent typing them out. */
    AGENT_DRAFT_LINES.forEach((_, idx) => {
      setTimeout(() => setReveal(idx + 1), 1100 + idx * 420)
    })
    setTimeout(() => setPhase('done'), 1100 + AGENT_DRAFT_LINES.length * 420 + 120)
    void i
  }

  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet phs-ask-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-ask-head">
            <span className="phs-ask-badge"><AgentBolt /></span>
            <div>
              <span className="phs-ask-eyebrow">Contract agent</span>
              <span className="phs-ask-title">What do you need drafted?</span>
            </div>
          </div>

          {phase === 'pick' && (
            <div className="phs-ask-prompts">
              {ASK_AGENT_PROMPTS.map((p, i) => (
                <button key={p} type="button" className="phs-ask-prompt" onClick={() => handlePick(i)} style={{ animationDelay: `${i * 60}ms` }}>
                  {p}
                  <ArrowRight />
                </button>
              ))}
              <div className="phs-ask-or">or type your own</div>
              <div className="phs-ask-input">
                <span className="phs-ask-caret" aria-hidden />
                <span className="phs-ask-placeholder">Service agreement with…</span>
              </div>
            </div>
          )}

          {(phase === 'thinking' || phase === 'drafting' || phase === 'done') && (
            <div className="phs-ask-draft">
              {phase === 'thinking' && (
                <div className="phs-ask-thinking">
                  <span className="phs-ask-dots"><span /><span /><span /></span>
                  <span className="phs-ask-thinking-label">Drafting…</span>
                </div>
              )}
              {AGENT_DRAFT_LINES.slice(0, reveal).map((line, i) => (
                <div key={i} className="phs-ask-line">
                  <span className="phs-ask-line-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="phs-ask-line-body">{line}</span>
                </div>
              ))}
              {phase === 'drafting' && reveal < AGENT_DRAFT_LINES.length && (
                <span className="phs-ask-caret phs-ask-caret--line" aria-hidden />
              )}
            </div>
          )}

          {phase === 'done' && (
            <div className="phs-tx-actions">
              <button type="button" className="phs-tx-action phs-tx-action--primary" onClick={requestClose}>Open draft</button>
              <button type="button" className="phs-tx-action" onClick={() => { setPhase('pick'); setReveal(0) }}>Try another</button>
            </div>
          )}
        </div>
      </div>
    </SheetPortal>
  )
}

/* ── Templates sheet — picker list of contract templates with icons.
   Lightweight; just shows the demo set with a "Use template" action. */
const TEMPLATES = [
  { name: 'Service agreement', sub: 'Most used · 12 contracts', initials: 'SA' },
  { name: 'NDA · mutual',      sub: 'Standard 2-party',         initials: 'ND' },
  { name: 'Freelance · hourly', sub: 'EUR · time-tracked',      initials: 'FR' },
  { name: 'Office sublease',   sub: 'Monthly rent + utilities', initials: 'OS' },
  { name: 'Vendor SoW',         sub: 'Milestone-based',         initials: 'VS' },
]
function TemplatesSheet({ onClose }: { onClose: () => void }) {
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head">
            <span className="phs-tx-logo"><StackMini /></span>
            <span className="phs-tx-merchant">Templates</span>
            <span className="phs-tx-amount" style={{ fontSize: 20 }}>5 in library</span>
            <span className="phs-tx-time">Pick one to start a draft</span>
          </div>
          <div className="phs-list" style={{ marginTop: 12 }}>
            {TEMPLATES.map((t, i) => (
              <button key={t.name} type="button" className="phs-row phs-row--button phs-row--stagger" style={{ animationDelay: `${40 + i * 70}ms` }} onClick={requestClose}>
                <span className="phs-row-logo phs-row-logo--mono">{t.initials}</span>
                <div className="phs-row-body">
                  <div className="phs-row-line1">
                    <span className="phs-row-title">{t.name}</span>
                    <Chevron />
                  </div>
                  <div className="phs-row-line2">
                    <span className="phs-row-sub">{t.sub}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}

/* ── Request payment sheet — mirror of PaymentSendSheet but inverted.
   Shows a mock QR code + amount + share strip; primary CTA is "Send
   link to customer". */
function RequestPaymentSheet({ onClose }: { onClose: () => void }) {
  const [sent, setSent] = useState(false)
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  const handleSend = () => {
    setSent(true)
    setTimeout(requestClose, 1400)
  }
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head">
            <span className="phs-tx-merchant">Request payment</span>
            <span className="phs-tx-amount" style={{ marginTop: 4 }}>€ 1 480.00</span>
            <span className="phs-tx-time">From: Atelje Nikolić · INV-2492</span>
          </div>
          <div className="phs-req-qr" aria-hidden>
            <div className="phs-req-qr-grid">
              {Array.from({ length: 36 }, (_, i) => (
                <span key={i} className={`phs-req-qr-cell${(i * 7) % 5 < 3 ? ' phs-req-qr-cell--on' : ''}`} />
              ))}
            </div>
            <div className="phs-req-qr-corner phs-req-qr-corner--tl" />
            <div className="phs-req-qr-corner phs-req-qr-corner--tr" />
            <div className="phs-req-qr-corner phs-req-qr-corner--bl" />
            <div className="phs-req-qr-logo">€</div>
          </div>
          <div className="phs-req-share">
            <span className="phs-req-share-label">Or share the link via</span>
            <div className="phs-req-share-row">
              <span className="phs-req-share-chip">Email</span>
              <span className="phs-req-share-chip">SMS</span>
              <span className="phs-req-share-chip">Copy</span>
              <span className="phs-req-share-chip">Slack</span>
            </div>
          </div>
          <div className="phs-tx-actions">
            <button type="button" className="phs-tx-action phs-tx-action--primary" onClick={handleSend} disabled={sent}>Send link to customer</button>
            <button type="button" className="phs-tx-action" onClick={requestClose} disabled={sent}>Cancel</button>
          </div>
        </div>
      </div>
      {sent && <SuccessScreen label="Link sent" />}
    </SheetPortal>
  )
}

/* ── Approve half-sheet — exact mirror of PhoneMockup's .sol-phone-sign
   panel. Sits at the BOTTOM of the phone, auto-height (not full-screen):
   chartreuse 'APPROVE PAYMENT' pill, bold title, party · amount, three
   skeleton doc lines, then swipe-to-approve track. After the user
   swipes through, the full-screen SuccessScreen takes over the entire
   phone (slides up from below, disc + check). Used for Studio Aria from
   the 'Needs your signature' action card. */
function ApproveSheet({
  eyebrow,
  title,
  counter,
  amount,
  onClose,
}: {
  eyebrow: string
  title: string
  counter: string
  amount: string
  onClose: () => void
}) {
  /* phase: 'idle' → 'signing' (signature draws across the doc) → 'signed'
     (chartreuse SuccessScreen takes over the phone) → auto-dismiss.
     The signature finishes drawing at ~1s; SuccessScreen enters right
     after so the user sees the pen-stroke first, THEN the green check. */
  const [phase, setPhase] = useState<'idle' | 'signing' | 'signed'>('idle')
  const [expanded, setExpanded] = useState(false)
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  /* Swipe-up on the grip → expand to fullscreen (when still half).
     When already expanded, no upward action; swipe-down still closes. */
  useSheetSwipe(sheetRef, requestClose, expanded ? undefined : () => setExpanded(true))
  const handleSwipe = () => {
    setPhase('signing')
    setTimeout(() => setPhase('signed'), 1100)
    setTimeout(() => requestClose(), 1100 + 1600)
  }
  /* Wrapper covers the whole phone bezel and pins the panel to the
     bottom — same layout as PhoneMockup's .sol-phone-sign (flex-end +
     22px padding-bottom). Panel overlaps the tabbar area; the backdrop
     dims the tabbar so this reads correctly. */
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div className={`phs-approve-wrap${expanded ? ' phs-approve-wrap--expanded' : ''}`}>
        <div
          ref={sheetRef}
          className={[
            'phs-approve-sheet',
            closing ? 'phs-approve-sheet--closing' : '',
            expanded ? 'phs-approve-sheet--expanded' : '',
            phase !== 'idle' ? 'phs-approve-sheet--signed' : '',
          ].filter(Boolean).join(' ')}
        >
          <div className="phs-approve-handle" aria-hidden />
          <span className="phs-approve-eyebrow">{eyebrow}</span>
          <div className="phs-approve-title">{title}</div>
          <div className="phs-approve-row">
            <span className="phs-approve-counter">{counter}</span>
            <span className="phs-approve-amount">{amount}</span>
          </div>

          {/* Tap the contract paper to expand the sheet to fullscreen
              and reveal the full document body. */}
          <button
            type="button"
            className="phs-approve-doc"
            onClick={() => setExpanded(true)}
            aria-label="Open full contract"
            disabled={expanded}
          >
            {expanded ? (
              <div className="phs-approve-doc-full">
                <div className="phs-approve-doc-clause">
                  <span className="phs-approve-doc-num">01</span>
                  <span>PARTIES — acc.tixo OÜ and {counter.split(' · ')[0]}, jointly “the parties”.</span>
                </div>
                <div className="phs-approve-doc-clause">
                  <span className="phs-approve-doc-num">02</span>
                  <span>SCOPE — Single payment for the service named above, settled on signature.</span>
                </div>
                <div className="phs-approve-doc-clause">
                  <span className="phs-approve-doc-num">03</span>
                  <span>AMOUNT — {amount}, net of any banking fees. Funds debited from the EUR Operating account.</span>
                </div>
                <div className="phs-approve-doc-clause">
                  <span className="phs-approve-doc-num">04</span>
                  <span>TIMING — Single transaction, instant SEPA settlement. Confirmation receipt issued to both parties.</span>
                </div>
                <div className="phs-approve-doc-clause">
                  <span className="phs-approve-doc-num">05</span>
                  <span>SIGNATURE — Biometric authorization by Marko Petrović constitutes a binding signature.</span>
                </div>
              </div>
            ) : (
              <>
                <span className="phs-approve-line" />
                <span className="phs-approve-line phs-approve-line--short" />
                <span className="phs-approve-line" />
                <svg viewBox="0 0 120 28" className="phs-approve-signature" aria-hidden>
                  <path
                    d="M 4 22 q 2 -10 6 -6 t 4 6 q 1 -14 6 -10 t 5 8 q 1 -16 7 -10 t 8 4 q 4 -8 9 -2 t 10 0 q 5 -6 10 -1 t 9 2 q 4 -4 9 -1 t 8 1 q 5 0 9 -2 q -2 6 -8 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="phs-approve-expand-hint">Tap to read</span>
              </>
            )}
          </button>

          {expanded && (
            <>
              {/* Dedicated signature block at the bottom of the contract,
                  labelled "Signed by" — the chartreuse pen-stroke draws
                  across the line when the user swipes-to-approve. */}
              <div className="phs-approve-sig-block">
                <span className="phs-approve-sig-label">Signed by Marko Petrović</span>
                <div className="phs-approve-sig-line">
                  <svg viewBox="0 0 120 28" className="phs-approve-signature phs-approve-signature--inline" aria-hidden>
                    <path
                      d="M 4 22 q 2 -10 6 -6 t 4 6 q 1 -14 6 -10 t 5 8 q 1 -16 7 -10 t 8 4 q 4 -8 9 -2 t 10 0 q 5 -6 10 -1 t 9 2 q 4 -4 9 -1 t 8 1 q 5 0 9 -2 q -2 6 -8 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="phs-approve-sig-date">Today · 09:41 · biometric</span>
              </div>

              <div className="phs-approve-meta">
                <div className="phs-approve-meta-row"><span>Counterparty</span><span>{counter.split(' · ')[0]}</span></div>
                <div className="phs-approve-meta-row"><span>From</span><span>EUR · Operating</span></div>
                <div className="phs-approve-meta-row"><span>Amount</span><span>{amount}</span></div>
                <div className="phs-approve-meta-row"><span>Reference</span><span>RNT-0524</span></div>
                <div className="phs-approve-meta-row"><span>Settlement</span><span>Instant SEPA</span></div>
              </div>
            </>
          )}

          <SlideToConfirm label="Swipe to approve" onConfirm={handleSwipe} />
        </div>
      </div>
      {phase === 'signed' && <SuccessScreen label="Approved" />}
    </SheetPortal>
  )
}

/* ── Convert sheet — FX from EUR to USD/RSD with live mid-market preview.
   Two cards stacked vertically: From (currency + amount) and To (computed
   amount + rate). Tap the swap icon between them to flip direction. */
function ConvertSheet({ onClose }: { onClose: () => void }) {
  const [direction, setDirection] = useState<'eur-usd' | 'usd-eur'>('eur-usd')
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  const rate = 1.0832
  const isFwd = direction === 'eur-usd'
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head"><span className="phs-tx-merchant">Convert</span></div>
          <div className="phs-conv-stack">
            <div className="phs-conv-card">
              <span className="phs-conv-label">From</span>
              <div className="phs-conv-row">
                <span className={`phs-wallet-flag phs-wallet-flag--${isFwd ? 'eu' : 'us'}`} />
                <span className="phs-conv-code">{isFwd ? 'EUR' : 'USD'}</span>
                <span className="phs-conv-amt">{isFwd ? '1 000.00' : '1 083.20'}</span>
              </div>
            </div>
            <button type="button" className="phs-conv-swap" onClick={() => setDirection(d => d === 'eur-usd' ? 'usd-eur' : 'eur-usd')} aria-label="Flip">
              <SwapMini />
            </button>
            <div className="phs-conv-card phs-conv-card--to">
              <span className="phs-conv-label">To</span>
              <div className="phs-conv-row">
                <span className={`phs-wallet-flag phs-wallet-flag--${isFwd ? 'us' : 'eu'}`} />
                <span className="phs-conv-code">{isFwd ? 'USD' : 'EUR'}</span>
                <span className="phs-conv-amt phs-conv-amt--in">{isFwd ? '1 083.20' : '1 000.00'}</span>
              </div>
            </div>
          </div>
          <div className="phs-conv-rate">
            Rate <strong>{rate.toFixed(4)}</strong> · mid-market · no fee
          </div>
          <div className="phs-tx-actions">
            <button type="button" className="phs-tx-action phs-tx-action--primary" onClick={requestClose}>Convert now</button>
            <button type="button" className="phs-tx-action" onClick={requestClose}>Save for later</button>
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}

/* ── Import sheet — file picker mock with drop zone, file types, recent.
   The drop zone is decorative (no real file handler) but reads as a
   working import surface. */
function ImportSheet({ onClose }: { onClose: () => void }) {
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head">
            <span className="phs-tx-merchant">Import contract</span>
            <span className="phs-tx-time">PDF · DOCX · Pages</span>
          </div>
          <div className="phs-import-drop">
            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M 16 4 v 16 M 10 14 l 6 6 l 6 -6" />
              <path d="M 6 24 v 4 h 20 v -4" />
            </svg>
            <span className="phs-import-drop-title">Drop a file here</span>
            <span className="phs-import-drop-sub">or tap to browse</span>
          </div>
          <div className="phs-tx-section-label">Recently imported</div>
          <div className="phs-list">
            {[
              { name: 'Studio Aria · MSA.pdf', sub: '124 KB · today' },
              { name: 'Atelje Nikolić · NDA.pdf', sub: '88 KB · May 9' },
            ].map((f, i) => (
              <div key={f.name} className="phs-row phs-row--stagger" style={{ animationDelay: `${40 + i * 80}ms` }}>
                <span className="phs-row-logo phs-row-logo--mono">PDF</span>
                <div className="phs-row-body">
                  <div className="phs-row-line1">
                    <span className="phs-row-title">{f.name}</span>
                  </div>
                  <div className="phs-row-line2">
                    <span className="phs-row-sub">{f.sub}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="phs-tx-actions">
            <button type="button" className="phs-tx-action phs-tx-action--primary" onClick={requestClose}>Choose file</button>
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}

/* ── Account detail sheet — opens when a wallet row is tapped on Home.
   Shows the full balance hero, IBAN, 7d cashflow split, and quick
   actions (Send / Request / Statement). Mirrors the same slide-up /
   slide-down behavior as the transaction sheet. */
function AccountDetailSheet({ account, onClose }: { account: AccountData; onClose: () => void }) {
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head">
            <span className={`phs-tx-logo phs-tx-logo--flag phs-tx-logo--${account.flag}`} aria-hidden />
            <span className="phs-tx-merchant">{account.code} · {account.sub}</span>
            <span className="phs-tx-amount">{account.amount}</span>
            <span className="phs-tx-time">{account.iban}</span>
          </div>

          <div className="phs-pay-foot phs-account-foot">
            <span className="phs-pay-foot-leg">
              In <span className="phs-pay-foot-value">{account.inflow}</span>
            </span>
            <span className="phs-pay-foot-leg phs-pay-foot-leg--out">
              Out <span className="phs-pay-foot-value">{account.outflow}</span>
            </span>
          </div>

          <div className="phs-tx-rows">
            <div className="phs-tx-row"><span>IBAN</span><span>{account.iban}</span></div>
            <div className="phs-tx-row"><span>Account</span><span>{account.code} · {account.sub}</span></div>
            <div className="phs-tx-row"><span>Available</span><span>{account.amount}</span></div>
            <div className="phs-tx-row"><span>Pending</span><span>—</span></div>
          </div>

          <div className="phs-tx-actions">
            <button type="button" className="phs-tx-action phs-tx-action--primary">Send from this account</button>
            <button type="button" className="phs-tx-action">Download statement</button>
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}

/* ── Profile sheet ─────────────────────────────────────────────────────
   Opens when the user taps the home greeting avatar or the More tab
   profile card. Same bottom-sheet motion as TxDetailSheet: portal'd to
   the bezel overlay layer, swipe-down to dismiss, X button on top-left.
   Centered avatar + name hero, then a stack of edit rows (Display name,
   Email, Org, Currency, Avatar) reading as "this is the live record"
   even though demo edits are not persisted. Sign-out at the bottom as
   a quiet danger row, matching the More tab's pattern. */
function ProfileSheet({ onClose }: { onClose: () => void }) {
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-profile-hero">
            <span className="phs-profile-hero-avatar">
              <img src="/marko.jpg" alt="" />
              <span className="phs-profile-hero-camera" aria-hidden>
                <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1.5" y="3.5" width="11" height="8" rx="1.5" />
                  <circle cx="7" cy="7.5" r="2" />
                  <path d="M 5 3.5 L 5.6 2.4 H 8.4 L 9 3.5" />
                </svg>
              </span>
            </span>
            <span className="phs-profile-hero-name">Marko Petrović</span>
            <span className="phs-profile-hero-org">acc.tixo · Operating EUR</span>
          </div>

          <span className="phs-group-label">Details</span>
          <div className="phs-group">
            <div className="phs-item phs-profile-row">
              <span className="phs-item-label">Display name</span>
              <span className="phs-item-meta">Marko Petrović</span>
              <Chevron />
            </div>
            <div className="phs-item phs-profile-row">
              <span className="phs-item-label">Email</span>
              <span className="phs-item-meta">marko@acc.tixo</span>
              <Chevron />
            </div>
            <div className="phs-item phs-profile-row">
              <span className="phs-item-label">Phone</span>
              <span className="phs-item-meta">+381 60 ··· 4017</span>
              <Chevron />
            </div>
          </div>

          <span className="phs-group-label">Workspace</span>
          <div className="phs-group">
            <div className="phs-item phs-profile-row">
              <span className="phs-item-label">Organization</span>
              <span className="phs-item-meta">acc.tixo d.o.o.</span>
              <Chevron />
            </div>
            <div className="phs-item phs-profile-row">
              <span className="phs-item-label">Default currency</span>
              <span className="phs-item-meta">EUR</span>
              <Chevron />
            </div>
            <div className="phs-item phs-profile-row">
              <span className="phs-item-label">Role</span>
              <span className="phs-item-meta">Owner</span>
            </div>
          </div>

          <div className="phs-tx-actions">
            <button type="button" className="phs-tx-action phs-tx-action--primary" onClick={requestClose}>Save changes</button>
            <button type="button" className="phs-tx-action phs-tx-action--danger" onClick={requestClose}>Sign out</button>
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}

/* ── Appearance sheet ─────────────────────────────────────────────────
   Quick-access version of MoreAppearanceView — triggered by the small
   palette button next to the home avatar. Same bottom-sheet motion.
   Body re-uses the picker grids + sliders so the user can tune theme,
   wallpaper, blur, and corner radius without leaving Home. */
function AppearanceSheet({ onClose }: { onClose: () => void }) {
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  const { theme, setTheme, wallpaper, setWallpaper, glassBlur, setGlassBlur, heroRadius, setHeroRadius, wallpaperBlur, setWallpaperBlur } = useContext(PhoneThemeContext)
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head phs-tx-head--simple">
            <span className="phs-tx-merchant">Customize Home</span>
          </div>

          <span className="phs-group-label">Theme</span>
          <div className="phs-theme-grid">
            {PHONE_THEMES.map(opt => (
              <button
                key={opt.id}
                type="button"
                className={`phs-theme-tile${theme === opt.id ? ' phs-theme-tile--active' : ''}`}
                onClick={() => setTheme(opt.id)}
                aria-pressed={theme === opt.id}
              >
                <div className="phs-theme-preview" data-phone-theme={opt.id} aria-hidden>
                  <div className="phs-theme-preview-bg" />
                  <div className="phs-theme-preview-greet">
                    <span className="phs-theme-preview-greet-name" />
                    <span className="phs-theme-preview-greet-avatar" />
                  </div>
                  <div className="phs-theme-preview-wallet"><span /><span /><span /></div>
                </div>
                <div className="phs-theme-tile-label">
                  <span className="phs-theme-tile-name">{opt.label}</span>
                  <span className="phs-theme-tile-sub">{opt.sub}</span>
                </div>
              </button>
            ))}
          </div>

          <span className="phs-group-label">Wallpaper</span>
          <div className="phs-theme-grid">
            {PHONE_WALLPAPERS.map(opt => (
              <button
                key={opt.id}
                type="button"
                className={`phs-theme-tile${wallpaper === opt.id ? ' phs-theme-tile--active' : ''}`}
                onClick={() => setWallpaper(opt.id)}
                aria-pressed={wallpaper === opt.id}
              >
                <div className="phs-theme-preview" data-phone-wallpaper={opt.id} aria-hidden>
                  <div className="phs-theme-preview-bg" />
                  <div className="phs-theme-preview-greet">
                    <span className="phs-theme-preview-greet-name" />
                    <span className="phs-theme-preview-greet-avatar" />
                  </div>
                  <div className="phs-theme-preview-wallet"><span /><span /><span /></div>
                </div>
                <div className="phs-theme-tile-label">
                  <span className="phs-theme-tile-name">{opt.label}</span>
                  <span className="phs-theme-tile-sub">{opt.sub}</span>
                </div>
              </button>
            ))}
          </div>

          {wallpaper !== 'none' && (
            <>
              <span className="phs-group-label">Wallpaper tuning</span>
              <div className="phs-group">
                <label className="phs-item phs-slider-row">
                  <span className="phs-item-label">Wallpaper blur</span>
                  <span className="phs-item-meta">{wallpaperBlur}px</span>
                  <input type="range" className="phs-slider" min={0} max={24} step={1} value={wallpaperBlur} onChange={e => setWallpaperBlur(Number(e.target.value))} />
                </label>
                <label className="phs-item phs-slider-row">
                  <span className="phs-item-label">Glass blur</span>
                  <span className="phs-item-meta">{glassBlur}px</span>
                  <input type="range" className="phs-slider" min={0} max={32} step={1} value={glassBlur} onChange={e => setGlassBlur(Number(e.target.value))} />
                </label>
                <label className="phs-item phs-slider-row">
                  <span className="phs-item-label">Corner radius</span>
                  <span className="phs-item-meta">{heroRadius}px</span>
                  <input type="range" className="phs-slider" min={0} max={32} step={1} value={heroRadius} onChange={e => setHeroRadius(Number(e.target.value))} />
                </label>
              </div>
            </>
          )}

          <div className="phs-tx-actions">
            <button type="button" className="phs-tx-action phs-tx-action--primary" onClick={requestClose}>Done</button>
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}

/* ── Contract detail sheet ─────────────────────────────────────────────
   Same bottom-sheet motion as TxDetailSheet. Surfaces contract metadata,
   the signers timeline, and a primary action whose label switches based
   on contract status (Sign / Open PDF). */
type ContractData = {
  party: string
  initials?: string
  logo?: string
  amount: string
  category: string
  status: 'Awaiting' | 'Signed' | 'Draft'
  signers: { name: string; signed: boolean }[]
  due?: string
  createdBy?: string
  pdfRef: string
}

function ContractDetailSheet({ contract, onClose }: { contract: ContractData; onClose: () => void }) {
  const [phase, setPhase] = useState<'idle' | 'signing' | 'signed'>('idle')
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)

  const handlePrimary = () => {
    if (contract.status !== 'Awaiting') return
    setPhase('signing')
    setTimeout(() => setPhase('signed'), 900)
    setTimeout(() => requestClose(), 2100)
  }

  const effectiveStatus = phase === 'signed' ? 'Signed' : contract.status
  const primaryLabel =
    phase === 'signing' ? 'Signing…'
    : phase === 'signed' ? 'Sealed'
    : effectiveStatus === 'Signed' ? 'Open PDF'
    : effectiveStatus === 'Draft' ? 'Continue draft'
    : 'Sign now'

  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head">
            <span className="phs-tx-logo">
              {contract.logo ? <img src={contract.logo} alt="" /> : <span>{contract.initials}</span>}
            </span>
            <span className="phs-tx-merchant">{contract.category}</span>
            <span className="phs-tx-amount">{contract.amount}</span>
            <span className="phs-tx-time">{contract.party}</span>
          </div>

          <div className={`phs-tx-status phs-tx-status--${effectiveStatus === 'Signed' ? 'done' : 'todo'}`}>
            {effectiveStatus === 'Signed' ? (<><CheckMini /> Sealed · all parties signed</>) : effectiveStatus === 'Awaiting' ? (<>Awaiting {contract.signers.filter(s => !s.signed).length} signature(s)</>) : (<>Draft · agent generated</>)}
          </div>

          {/* Glass-contract paper object — mirrors PhoneMockup's
              .sol-glass-contract surface (cream paper, soft shadow,
              dashed divider above the signature). Sits inside the
              sheet so the contract reads as a real document, not a
              metadata table.
              Note: phase-signed success screen is rendered OUTSIDE the
              sheet (below) so it covers the whole phone, not just the
              sheet bounds. */}
          <div className={`phs-glass-doc${effectiveStatus === 'Signed' ? ' phs-glass-doc--signed' : ''}`}>
            <span className="phs-glass-meta">{contract.pdfRef} · {contract.party}</span>
            <span className={`phs-glass-pill phs-glass-pill--${effectiveStatus === 'Signed' ? 'signed' : effectiveStatus === 'Awaiting' ? 'draft' : 'draft'}`}>
              {effectiveStatus === 'Signed' ? 'Signed' : effectiveStatus === 'Awaiting' ? 'Awaiting' : 'Draft'}
            </span>
            <span className="phs-glass-title">{contract.category}</span>
            <span className="phs-glass-line" />
            <span className="phs-glass-line phs-glass-line--short" />
            <span className="phs-glass-line" />
            <svg viewBox="0 0 120 28" className="phs-glass-sig" aria-hidden>
              <path
                d="M 4 22 q 2 -10 6 -6 t 4 6 q 1 -14 6 -10 t 5 8 q 1 -16 7 -10 t 8 4 q 4 -8 9 -2 t 10 0 q 5 -6 10 -1 t 9 2 q 4 -4 9 -1 t 8 1 q 5 0 9 -2 q -2 6 -8 5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="phs-tx-rows">
            <div className="phs-tx-row"><span>Party</span><span>{contract.party}</span></div>
            <div className="phs-tx-row"><span>Category</span><span>{contract.category}</span></div>
            <div className="phs-tx-row"><span>Amount</span><span>{contract.amount}</span></div>
            {contract.due && <div className="phs-tx-row"><span>Due</span><span>{contract.due}</span></div>}
            <div className="phs-tx-row"><span>Reference</span><span>{contract.pdfRef}</span></div>
            {contract.createdBy && <div className="phs-tx-row"><span>Created by</span><span>{contract.createdBy}</span></div>}
          </div>

          <div className="phs-tx-section-label">Signers</div>
          <div className="phs-tx-timeline">
            {contract.signers.map((s) => (
              <div key={s.name} className={`phs-tx-step ${s.signed ? 'phs-tx-step--done' : 'phs-tx-step--pending'}`}>
                {s.name} {s.signed ? '· signed' : '· awaiting'}
              </div>
            ))}
          </div>

          <div className="phs-tx-actions">
            {contract.status === 'Awaiting' && phase === 'idle' && (
              <SlideToConfirm label="Swipe to sign" onConfirm={handlePrimary} />
            )}
            {contract.status === 'Awaiting' && phase !== 'idle' && (
              <div className={`phs-tx-action phs-tx-action--primary phs-sign phs-sign--${phase}`}>
                {phase === 'signing' && <span className="phs-sign-spinner" aria-hidden />}
                {phase === 'signed' && (
                  <svg className="phs-sign-check" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path d="M 3 7.2 L 5.8 9.8 L 11 4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {primaryLabel}
              </div>
            )}
            {contract.status !== 'Awaiting' && (
              <button type="button" className="phs-tx-action phs-tx-action--primary">
                {primaryLabel}
              </button>
            )}
            <button type="button" className="phs-tx-action" disabled={phase !== 'idle'}>Forward to lawyer</button>
          </div>
        </div>
      </div>
      {phase === 'signed' && <SuccessScreen label="Signed" />}
    </SheetPortal>
  )
}

/* ── Invoice detail sheet ──────────────────────────────────────────────
   Sibling of ContractDetailSheet. Where contracts are about signing,
   invoices are about getting paid — so the surface emphasizes:
     1. line-items (what's billed),
     2. cash-flow forecast ("expected by, will affect Operating €"),
     3. linked transaction (the bank line that paid it, or empty state),
     4. reminders timeline (when + how the customer was nudged).
   Primary CTA is "Send reminder" while outstanding/overdue; flips to
   "Open PDF" once paid. Tapped from any TxDetailSheet whose tx carries
   an invoice ref. */
type InvoiceLineItem = { label: string; amount: string }
type InvoiceReminder = { channel: 'email' | 'sms' | 'call'; date: string; result: 'sent' | 'opened' | 'replied' }
type InvoiceLinkedTx = { merchant: string; logo?: string; amount: string; time: string }
type InvoiceData = {
  ref: string
  party: string
  initials?: string
  logo?: string
  amount: string
  category: string
  status: 'Outstanding' | 'Paid' | 'Overdue' | 'Draft'
  issued?: string
  due?: string
  lineItems: InvoiceLineItem[]
  forecastNote?: string
  linkedTx?: InvoiceLinkedTx
  reminders: InvoiceReminder[]
}

const INVOICES: Record<string, InvoiceData> = {
  'INV-2491': {
    ref: 'INV-2491',
    party: 'Stripe (March payout)',
    logo: '/logo-stripe.png',
    amount: '€ 1 240.00',
    category: 'Stripe payout · March',
    status: 'Paid',
    issued: 'Apr 28',
    due: 'May 5',
    lineItems: [
      { label: 'Subscription revenue · 18 charges', amount: '€ 1 084.00' },
      { label: 'One-time charges · 7 sales',        amount: '€ 198.00' },
      { label: 'Stripe processing fee',              amount: '−€ 42.00' },
    ],
    forecastNote: 'Cleared 09:41 · Operating € balance updated',
    linkedTx: {
      merchant: 'Stripe payout',
      logo: '/logo-stripe.png',
      amount: '+€ 1 240',
      time: 'Today · 09:41',
    },
    reminders: [],
  },
  'INV-2492': {
    ref: 'INV-2492',
    party: 'Atelje Nikolić',
    initials: 'AN',
    amount: '€ 1 480.00',
    category: 'Brand identity · final',
    status: 'Outstanding',
    issued: 'May 1',
    due: 'May 15',
    lineItems: [
      { label: 'Identity system · 3 marks',  amount: '€ 1 200.00' },
      { label: 'Typography license · 1 yr',   amount: '€ 280.00' },
    ],
    forecastNote: 'Expected by May 15 · +€ 1 480 to Operating EUR',
    reminders: [
      { channel: 'email', date: 'May 8',  result: 'opened' },
      { channel: 'email', date: 'May 11', result: 'sent' },
    ],
  },
  'INV-2026-091': {
    ref: 'INV-2026-091',
    party: 'Studio Aria d.o.o.',
    initials: 'SA',
    amount: '€ 124 850.00',
    category: 'Service agreement · April',
    status: 'Paid',
    issued: 'Apr 30',
    due: 'May 10',
    lineItems: [
      { label: 'Retainer · April', amount: '€ 100 000.00' },
      { label: 'Overage · 18.5h',  amount: '€ 24 850.00' },
    ],
    forecastNote: 'Cleared this morning · matched within 14s of bank notification',
    linkedTx: {
      merchant: 'Studio Aria d.o.o.',
      amount: '+€ 124 850',
      time: 'Today · 09:38',
    },
    reminders: [
      { channel: 'email', date: 'May 7', result: 'opened' },
    ],
  },
  'INV-2487': {
    ref: 'INV-2487',
    party: 'Voli rentals',
    initials: 'VR',
    amount: '€ 350.00',
    category: 'Office rent · April',
    status: 'Overdue',
    issued: 'Apr 1',
    due: 'Apr 30',
    lineItems: [
      { label: 'Coworking desk · monthly', amount: '€ 350.00' },
    ],
    forecastNote: 'Overdue by 12 days · 64% probability customer pays after 2nd reminder',
    reminders: [
      { channel: 'email', date: 'May 1', result: 'opened' },
      { channel: 'email', date: 'May 7', result: 'sent' },
      { channel: 'sms',   date: 'May 11', result: 'sent' },
    ],
  },
}

function ForecastBolt() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 7.5 1.5 L 3 8 h 3 L 5 12.5 L 10 6 h -3 z" />
    </svg>
  )
}

function InvoiceDetailSheet({ invoice, onClose }: { invoice: InvoiceData; onClose: () => void }) {
  const [phase, setPhase] = useState<'idle' | 'reminding' | 'sent'>('idle')
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)

  const canRemind = invoice.status === 'Outstanding' || invoice.status === 'Overdue'
  const handleRemind = () => {
    if (!canRemind) return
    setPhase('reminding')
    setTimeout(() => setPhase('sent'), 900)
    setTimeout(() => requestClose(), 2100)
  }

  const primaryLabel =
    phase === 'reminding' ? 'Sending…'
    : phase === 'sent' ? 'Reminder sent'
    : invoice.status === 'Paid' ? 'Open PDF'
    : invoice.status === 'Draft' ? 'Continue draft'
    : invoice.status === 'Overdue' ? 'Send firmer reminder'
    : 'Send reminder'

  const statusVariant: 'done' | 'warn' | 'todo' =
    invoice.status === 'Paid' ? 'done'
    : invoice.status === 'Overdue' ? 'warn'
    : 'todo'

  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head">
            <span className="phs-tx-logo">
              {invoice.logo ? <img src={invoice.logo} alt="" /> : <span>{invoice.initials}</span>}
            </span>
            <span className="phs-tx-merchant">{invoice.category}</span>
            <span className="phs-tx-amount">{invoice.amount}</span>
            <span className="phs-tx-time">{invoice.party} · {invoice.ref}</span>
          </div>

          <div className={`phs-tx-status phs-tx-status--${statusVariant}`}>
            {invoice.status === 'Paid' && (<><CheckMini /> Paid · matched to bank line</>)}
            {invoice.status === 'Overdue' && (<>Overdue · {invoice.reminders.length} reminders sent</>)}
            {invoice.status === 'Outstanding' && (<>Outstanding · due {invoice.due}</>)}
            {invoice.status === 'Draft' && (<>Draft · finish to send</>)}
          </div>

          {/* Glass paper invoice — same paper-surface family as
              .phs-glass-doc but body holds line-items + total instead
              of a signature stroke. */}
          <div className={`phs-glass-doc${invoice.status === 'Paid' ? ' phs-glass-doc--signed' : ''}`}>
            <span className="phs-glass-meta">{invoice.ref} · {invoice.party}</span>
            <span className={`phs-glass-pill phs-glass-pill--${invoice.status === 'Paid' ? 'signed' : invoice.status === 'Overdue' ? 'warn' : 'draft'}`}>
              {invoice.status}
            </span>
            <span className="phs-glass-title">{invoice.category}</span>
            <div className="phs-glass-items">
              {invoice.lineItems.map((li, i) => (
                <div key={i} className="phs-glass-item">
                  <span className="phs-glass-item-label">{li.label}</span>
                  <span className="phs-glass-item-amount">{li.amount}</span>
                </div>
              ))}
            </div>
            <span className="phs-glass-line phs-glass-line--short" />
            <div className="phs-glass-total">
              <span>Total</span>
              <span>{invoice.amount}</span>
            </div>
          </div>

          <div className="phs-tx-rows">
            <div className="phs-tx-row"><span>Party</span><span>{invoice.party}</span></div>
            <div className="phs-tx-row"><span>Issued</span><span>{invoice.issued ?? '—'}</span></div>
            <div className="phs-tx-row"><span>Due</span><span>{invoice.due ?? '—'}</span></div>
            <div className="phs-tx-row"><span>Reference</span><span>{invoice.ref}</span></div>
          </div>

          {invoice.forecastNote && (
            <>
              <div className="phs-tx-section-label">Forecast</div>
              <div className={`phs-tx-forecast phs-tx-forecast--${statusVariant}`}>
                <span className="phs-tx-forecast-icon"><ForecastBolt /></span>
                <span className="phs-tx-forecast-text">{invoice.forecastNote}</span>
              </div>
            </>
          )}

          <div className="phs-tx-section-label">Bank linkage</div>
          {invoice.linkedTx ? (
            <div className="phs-tx-linked">
              <span className="phs-row-logo">
                {invoice.linkedTx.logo ? <img src={invoice.linkedTx.logo} alt="" /> : <span>{invoice.initials}</span>}
              </span>
              <div className="phs-row-body">
                <div className="phs-row-line1">
                  <span className="phs-row-title">{invoice.linkedTx.merchant}</span>
                  <span className="phs-row-amount phs-row-amount--in">{invoice.linkedTx.amount}</span>
                </div>
                <div className="phs-row-substatus phs-row-substatus--done">
                  <CheckMini /> Matched · {invoice.linkedTx.time}
                </div>
              </div>
            </div>
          ) : (
            <div className="phs-tx-linked phs-tx-linked--empty">
              Agent will auto-match this within 1 day of payment landing.
            </div>
          )}

          {invoice.reminders.length > 0 && (
            <>
              <div className="phs-tx-section-label">Reminders</div>
              <div className="phs-tx-timeline">
                {invoice.reminders.map((r, i) => (
                  <div key={i} className="phs-tx-step phs-tx-step--done">
                    {r.channel === 'email' ? 'Email' : r.channel === 'sms' ? 'SMS' : 'Call'} · {r.date} · {r.result}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="phs-tx-actions">
            {canRemind && phase === 'idle' && (
              <button type="button" className="phs-tx-action phs-tx-action--primary" onClick={handleRemind}>{primaryLabel}</button>
            )}
            {phase !== 'idle' && (
              <div className={`phs-tx-action phs-tx-action--primary phs-sign phs-sign--${phase === 'reminding' ? 'signing' : 'signed'}`}>
                {phase === 'reminding' && <span className="phs-sign-spinner" aria-hidden />}
                {phase === 'sent' && (
                  <svg className="phs-sign-check" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path d="M 3 7.2 L 5.8 9.8 L 11 4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {primaryLabel}
              </div>
            )}
            {!canRemind && phase === 'idle' && (
              <button type="button" className="phs-tx-action phs-tx-action--primary">{primaryLabel}</button>
            )}
            <button type="button" className="phs-tx-action" disabled={phase !== 'idle'}>Share link</button>
          </div>
        </div>
      </div>
      {phase === 'sent' && <SuccessScreen label="Reminder sent" />}
    </SheetPortal>
  )
}

/* ── Slide-to-confirm — proper drag-to-confirm slider (iOS slide-to-
   power-off pattern). Drag the chartreuse thumb to the right end of
   the pill track to fire onConfirm. Track fill grows with thumb. */
function SlideToConfirm({ label, onConfirm }: { label: string; onConfirm: () => void }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)
  const startX = useRef<number | null>(null)
  const max = useRef(0)
  const armed = useRef(false)
  const [done, setDone] = useState(false)

  const setOffset = (x: number) => {
    if (!thumbRef.current || !fillRef.current) return
    thumbRef.current.style.transform = `translateX(${x}px)`
    fillRef.current.style.transform = `scaleX(${Math.min(1, (x + 44) / Math.max(1, max.current + 44))})`
  }
  const reset = () => {
    if (!thumbRef.current || !fillRef.current) return
    thumbRef.current.style.transition = 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)'
    fillRef.current.style.transition = 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)'
    thumbRef.current.style.transform = 'translateX(0)'
    fillRef.current.style.transform = 'scaleX(0)'
    window.setTimeout(() => {
      if (thumbRef.current) thumbRef.current.style.transition = ''
      if (fillRef.current) fillRef.current.style.transition = ''
    }, 320)
  }

  useEffect(() => {
    const track = trackRef.current
    const thumb = thumbRef.current
    if (!track || !thumb) return
    const onDown = (e: PointerEvent) => {
      if (done) return
      /* Stop the bubble so the parent sheet's swipe-to-dismiss hook
         doesn't also register this as a pending vertical drag. */
      e.stopPropagation()
      startX.current = e.clientX
      armed.current = true
      max.current = track.offsetWidth - thumb.offsetWidth - 8
      try { thumb.setPointerCapture(e.pointerId) } catch (_) { /* noop */ }
    }
    const onMove = (e: PointerEvent) => {
      if (startX.current == null || !armed.current) return
      const x = Math.max(0, Math.min(max.current, e.clientX - startX.current))
      setOffset(x)
    }
    const onUp = (e: PointerEvent) => {
      if (startX.current == null) return
      const x = Math.max(0, Math.min(max.current, e.clientX - startX.current))
      if (x >= max.current - 4) {
        setOffset(max.current)
        setDone(true)
        window.setTimeout(onConfirm, 80)
      } else {
        reset()
      }
      startX.current = null
      armed.current = false
    }
    thumb.addEventListener('pointerdown', onDown)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      thumb.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [done, onConfirm])

  return (
    <div ref={trackRef} className={`phs-slide-track${done ? ' phs-slide-track--done' : ''}`}>
      <span ref={fillRef} className="phs-slide-fill" />
      <span className="phs-slide-label">{label}</span>
      <div ref={thumbRef} className="phs-slide-thumb" aria-label="Slide to confirm" role="slider">
        <svg viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M 3 7 H 11 M 7.5 3.5 L 11 7 L 7.5 10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  )
}

/* ── Payment send sheet — opens when the user taps the Send tile.
   Carries the most "premium" animation on the surface: recipient hero,
   amount with mock numeric pad, then a 4-phase confirm animation:
   idle → sending (spinner ring) → sent (chartreuse fill + stroke-dash
   check) → auto-dismiss. */
function PaymentSendSheet({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<'compose' | 'sending' | 'sent'>('compose')
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)

  const handleSend = () => {
    if (phase !== 'compose') return
    setPhase('sending')
    setTimeout(() => setPhase('sent'), 1100)
    setTimeout(() => requestClose(), 2400)
  }

  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet phs-send-sheet phs-send-sheet--${phase}${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll phs-send-scroll">
          <div className="phs-send-recip">
            <div className="phs-send-recip-avatar"><span>SA</span></div>
            <div className="phs-send-recip-body">
              <span className="phs-send-recip-label">Send to</span>
              <span className="phs-send-recip-name">Studio Aria d.o.o.</span>
              <span className="phs-send-recip-iban">RS35 — 6660 ···· 2014</span>
            </div>
          </div>
          <div className="phs-send-amount">
            <span className="phs-send-amount-cur">€</span>
            <span className="phs-send-amount-num">4 800.00</span>
          </div>
          <div className="phs-send-rate">EUR · same-day SEPA · no fee</div>
          <div className="phs-send-from">
            <span className="phs-send-from-label">From</span>
            <span className="phs-send-from-row">
              <span className="phs-wallet-flag phs-wallet-flag--eu" />
              <span className="phs-send-from-name">Operating · EUR</span>
              <span className="phs-send-from-bal">€ 4 826.40</span>
            </span>
          </div>
          <div className="phs-send-stage">
            {phase === 'sending' && (
              <>
                <span className="phs-send-ring" aria-hidden />
                <span className="phs-send-stage-line">Sending…</span>
              </>
            )}
          </div>
          {phase === 'compose' && (
            <div className="phs-tx-actions">
              <SlideToConfirm label="Slide to send · € 4 800.00" onConfirm={handleSend} />
              <button type="button" className="phs-tx-action" onClick={requestClose}>Cancel</button>
            </div>
          )}
        </div>
      </div>
      {phase === 'sent' && <SuccessScreen label="Sent · arrives in seconds" />}
    </SheetPortal>
  )
}

/* ── Scan overlay — opens the ScanToPay tutorial as a full sheet ──── */
function ScanOverlay({ onClose }: { onClose: () => void }) {
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-scan-overlay phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-scan-overlay-body">
          <div className="phs-scan-overlay-glyph" aria-hidden>
            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M 4 10 V 6 a 2 2 0 0 1 2 -2 H 10" />
              <path d="M 22 4 H 26 a 2 2 0 0 1 2 2 V 10" />
              <path d="M 28 22 V 26 a 2 2 0 0 1 -2 2 H 22" />
              <path d="M 10 28 H 6 a 2 2 0 0 1 -2 -2 V 22" />
              <circle cx="16" cy="16" r="5" />
              <path d="M 21 16 h 2" />
            </svg>
          </div>
          <div className="phs-scan-overlay-title">Scan a bill</div>
          <div className="phs-scan-overlay-body-text">
            Forward by email, paste a screenshot, or snap a photo.<br />
            The agent will extract recipient, amount, and reference.
          </div>
          <div className="phs-scan-overlay-steps">
            <span><strong>1.</strong> Forward</span>
            <span><strong>2.</strong> Extract</span>
            <span><strong>3.</strong> Review</span>
            <span><strong>4.</strong> Send</span>
          </div>
          <button type="button" className="phs-tx-action phs-tx-action--primary">Open camera</button>
        </div>
      </div>
    </SheetPortal>
  )
}

/* Studio Aria is the "Needs your signature" hero contract — referenced
   both from the action-card Sign button and from the first row in the
   list. Lifted to a constant so tapping either entry-point opens the
   same ContractDetailSheet with the same data (and the same swipe-to-
   sign flow as in PhoneMockup's Approve sheet). */
const STUDIO_ARIA: ContractData = {
  party: 'Studio Aria d.o.o.',
  initials: 'SA',
  amount: '€ 4 800',
  category: 'Service agreement',
  status: 'Awaiting',
  signers: [
    { name: 'Marko Petrović', signed: true },
    { name: 'Studio Aria d.o.o.', signed: false },
  ],
  due: 'May 18',
  createdBy: 'Marko · today',
  pdfRef: 'CT-2026-014',
}

/* All contracts shown in the Contracts list, in render order. Pulled
   out of inline JSX so the segmented filter (All / Pending / Signed)
   can actually filter — previously the segmented control was decorative
   spans with no state behind them. Extra `subLine` carries the per-row
   secondary text the list shows below the title. */
type ContractListRow = ContractData & { subLine: string; amountBlur?: boolean }
const CONTRACT_LIST: ContractListRow[] = [
  { ...STUDIO_ARIA, subLine: 'Service · 2 signers' },
  {
    party: 'Atelje Nikolić',
    initials: 'AN',
    amount: '€ 12 400',
    category: 'NDA',
    status: 'Signed',
    signers: [
      { name: 'Marko Petrović', signed: true },
      { name: 'Atelje Nikolić', signed: true },
    ],
    createdBy: 'Marko · May 9',
    pdfRef: 'CT-2026-011',
    subLine: 'NDA · sealed May 9',
    amountBlur: true,
  },
  {
    party: 'Stripe',
    logo: '/logo-stripe.png',
    amount: '€ 0',
    category: 'TOS renewal',
    status: 'Awaiting',
    signers: [{ name: 'Marko Petrović', signed: false }],
    due: 'May 15',
    createdBy: 'Stripe · auto',
    pdfRef: 'CT-2026-008',
    subLine: 'Renewal · due May 15',
  },
  {
    party: 'Petrović Konsalting',
    initials: 'PK',
    amount: '€ 1 200',
    category: 'Service agreement',
    status: 'Draft',
    signers: [{ name: 'Pending', signed: false }],
    createdBy: 'Agent · today',
    pdfRef: 'CT-2026-015',
    subLine: 'Service · agent-generated',
  },
]
type ContractFilter = 'all' | 'pending' | 'signed'
function filterContracts(rows: ContractListRow[], f: ContractFilter): ContractListRow[] {
  if (f === 'all') return rows
  if (f === 'pending') return rows.filter(r => r.status === 'Awaiting' || r.status === 'Draft')
  return rows.filter(r => r.status === 'Signed')
}

function ContractsScreen() {
  const [selected, setSelected] = useState<ContractData | null>(null)
  const [showScan, setShowScan] = useState(false)
  const [showAsk, setShowAsk] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [showApprove, setShowApprove] = useState(false)
  const [filter, setFilter] = useState<ContractFilter>('all')
  const rows = filterContracts(CONTRACT_LIST, filter)

  return (
    <>
      <div className="phs-screen-head">
        <span className="phs-screen-title">Contracts</span>
        <span className="phs-screen-count">12 active</span>
      </div>

      {/* Two large task tiles for the most common contract starts: ask
          the agent to draft from a one-line prompt, or open the template
          library. Secondary inputs (Import existing PDF, Scan a paper)
          collapse to a slim chip row below — same hierarchy as Payments. */}
      <div className="phs-pay-tiles">
        <button type="button" className="phs-pay-tile phs-pay-tile--primary" onClick={() => setShowAsk(true)}>
          <span className="phs-pay-tile-icon"><SparkleMini /></span>
          <span className="phs-pay-tile-label">Ask agent</span>
          <span className="phs-pay-tile-sub">Describe → draft</span>
        </button>
        <button type="button" className="phs-pay-tile" onClick={() => setShowTemplates(true)}>
          <span className="phs-pay-tile-icon"><StackMini /></span>
          <span className="phs-pay-tile-label">Templates</span>
          <span className="phs-pay-tile-sub">5 in your library</span>
        </button>
      </div>
      <div className="phs-chips phs-chips--secondary">
        <button type="button" className="phs-chip" onClick={() => setShowImport(true)}>
          <span className="phs-chip-icon"><DownloadMini /></span>
          Import PDF
        </button>
        <button type="button" className="phs-chip" onClick={() => setShowScan(true)}>
          <span className="phs-chip-icon"><ScanMini /></span>
          Scan paper
        </button>
      </div>

      {/* "Needs your signature" hero — surfaces the one contract that
          requires the user right now, with a chartreuse Sign CTA. Drives
          the only mandatory action without scrolling. */}
      <div className="phs-action-card">
        <div className="phs-action-card-head">
          <span className="phs-action-card-label">Needs your signature</span>
          <span className="phs-action-card-count">1</span>
        </div>
        <div className="phs-action-card-row">
          <span className="phs-row-logo phs-row-logo--mono">SA</span>
          <div className="phs-row-body">
            <div className="phs-row-title">Studio Aria d.o.o.</div>
            <div className="phs-row-sub">€ 4 800 · service agreement</div>
          </div>
          <button type="button" className="phs-action-card-cta" onClick={() => setShowApprove(true)}>Sign</button>
        </div>
      </div>

      <div className="phs-segmented" role="tablist">
        {(['all', 'pending', 'signed'] as ContractFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filter === f}
            className={`phs-segmented-item${filter === f ? ' phs-segmented-item--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'pending' ? 'Pending' : 'Signed'}
          </button>
        ))}
      </div>

      <div className="phs-list">
        {rows.length === 0 && (
          <div className="phs-list-empty">No contracts in this view.</div>
        )}
        {rows.map((row, i) => {
          const pillClass =
            row.status === 'Signed' ? 'phs-pill--signed'
            : row.status === 'Draft' ? 'phs-pill--draft'
            : 'phs-pill--pending'
          const title = row.party === 'Stripe' ? 'Stripe — TOS update' : row.party
          return (
            <button
              key={row.pdfRef}
              type="button"
              className="phs-row phs-row--button phs-row--stagger"
              style={{ animationDelay: `${40 + i * 60}ms` }}
              onClick={() => setSelected(row)}
            >
              <span className={`phs-row-logo${row.logo ? '' : ' phs-row-logo--mono'}`}>
                {row.logo ? <img src={row.logo} alt="" /> : row.initials}
              </span>
              <div className="phs-row-body">
                <div className="phs-row-line1">
                  <span className="phs-row-title">{title}</span>
                  <span className={`phs-pill ${pillClass}`}>{row.status === 'Awaiting' ? 'Awaiting' : row.status}</span>
                </div>
                <div className="phs-row-line2">
                  <span className="phs-row-sub">{row.subLine}</span>
                  <span className={`phs-row-amount${row.amountBlur ? ' phs-row-amount--blur' : ''}`} {...(row.amountBlur ? { 'aria-label': 'Amount hidden' } : {})}>{row.amount}</span>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {selected && <ContractDetailSheet contract={selected} onClose={() => setSelected(null)} />}
      {showScan && <ScanOverlay onClose={() => setShowScan(false)} />}
      {showAsk && <AskAgentSheet onClose={() => setShowAsk(false)} />}
      {showTemplates && <TemplatesSheet onClose={() => setShowTemplates(false)} />}
      {showImport && <ImportSheet onClose={() => setShowImport(false)} />}
      {showApprove && (
        <ApproveSheet
          eyebrow="Approve payment"
          title="Office rent"
          counter="Voli rentals · today"
          amount="€ 350.00"
          onClose={() => setShowApprove(false)}
        />
      )}
    </>
  )
}

/* ── Transaction detail sheet ────────────────────────────────────────
   Bottom-sheet overlay that covers ~90% of the phone screen when a
   transaction row is tapped. Animates from the row's tap point via
   transform-origin so it feels like the row itself "opens up". Inside
   the sheet is scrollable so we can carry rich metadata (timeline,
   reference, account, FX rate) without expanding the row inline. */
type TxData = {
  merchant: string
  logo?: string
  initials?: string
  amount: string
  direction: 'in' | 'out'
  matched: boolean
  invoice?: string
  category: string
  time: string
  account: string
}

type SelectedTx = TxData

function TxDetailSheet({ tx, onClose, onOpenInvoice }: { tx: SelectedTx; onClose: () => void; onOpenInvoice?: (ref: string) => void }) {
  const isIn = tx.direction === 'in'
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  const hasInvoiceLink = !!(tx.matched && tx.invoice && INVOICES[tx.invoice])
  const handlePrimary = () => {
    if (!tx.invoice || !onOpenInvoice) return
    if (!INVOICES[tx.invoice]) return
    // Stack the invoice sheet on top of the tx sheet (don't close
    // tx sheet) — same as iOS NavigationView push. Closing invoice
    // sheet brings the user back to the tx detail.
    onOpenInvoice(tx.invoice)
  }
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-tx-head">
            <span className="phs-tx-logo">
              {tx.logo ? <img src={tx.logo} alt="" /> : <span>{tx.initials}</span>}
            </span>
            <span className="phs-tx-merchant">{tx.merchant}</span>
            <span className={`phs-tx-amount${isIn ? ' phs-tx-amount--in' : ''}`}>{tx.amount}</span>
            <span className="phs-tx-time">{tx.time}</span>
          </div>

          <div className={`phs-tx-status phs-tx-status--${tx.matched ? 'done' : 'todo'}`}>
            {tx.matched ? (
              <>
                <CheckMini /> Matched with {tx.invoice}
              </>
            ) : (
              <>Awaiting match · 1 candidate</>
            )}
          </div>

          <div className="phs-tx-rows">
            <div className="phs-tx-row"><span>Category</span><span>{tx.category}</span></div>
            <div className="phs-tx-row"><span>Account</span><span>{tx.account}</span></div>
            <div className="phs-tx-row"><span>Reference</span><span>{tx.invoice ?? '—'}</span></div>
            <div className="phs-tx-row"><span>Settlement</span><span>Same day</span></div>
            <div className="phs-tx-row"><span>Fee</span><span>€ 0.00</span></div>
            <div className="phs-tx-row"><span>FX rate</span><span>—</span></div>
          </div>

          <div className="phs-tx-section-label">Activity</div>
          <div className="phs-tx-timeline">
            <div className="phs-tx-step phs-tx-step--done">Received via bank · 09:41</div>
            <div className="phs-tx-step phs-tx-step--done">Categorised by agent</div>
            {tx.matched ? (
              <div className="phs-tx-step phs-tx-step--done">Matched to {tx.invoice}</div>
            ) : (
              <div className="phs-tx-step phs-tx-step--pending">Awaiting your match</div>
            )}
            <div className={`phs-tx-step ${tx.matched ? 'phs-tx-step--done' : 'phs-tx-step--pending'}`}>Posted to ledger</div>
          </div>

          <div className="phs-tx-actions">
            <button
              type="button"
              className="phs-tx-action phs-tx-action--primary"
              onClick={handlePrimary}
              disabled={tx.matched && !hasInvoiceLink}
            >
              {tx.matched ? 'Open invoice' : 'Match now'}
            </button>
            <button type="button" className="phs-tx-action">Reassign category</button>
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}

/* ── Tx summary sheet ──────────────────────────────────────────────────
   Compact analytics modal opened from "See all" links and reconciliation
   hero taps. Shows a single donut (filled portion = the metric being
   summarized, e.g. reconciled / total), a 2-column stats strip, and the
   full underlying row list. The row tap callback returns the user to the
   regular tx-detail flow (sheets stack — closing summary returns to it). */
type TxSummary = {
  title: string
  subtitle: string
  donutLabel: string
  donutFraction: number  // 0..1 — how much of the ring fills chartreuse
  donutCenterValue: string
  donutCenterCaption: string
  stats: { label: string; value: string; tone?: 'positive' | 'neutral' | 'warn' }[]
  rows: TxData[]
}
function TxSummarySheet({ data, onClose, onPickTx }: { data: TxSummary; onClose: () => void; onPickTx: (tx: TxData) => void }) {
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  /* SVG donut math — r=32 → circumference = 201.06. Filled arc length
     proportional to `donutFraction`. The unfilled portion stays as
     muted edge color for context. */
  const r = 32
  const c = 2 * Math.PI * r
  const filled = Math.max(0, Math.min(1, data.donutFraction)) * c
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-tx-close" onClick={requestClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M 4 4 L 10 10 M 10 4 L 4 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="phs-tx-scroll">
          <div className="phs-summary-head">
            <div className="phs-donut" role="img" aria-label={`${data.donutLabel}: ${data.donutCenterValue}`}>
              <svg viewBox="0 0 80 80">
                <circle cx="40" cy="40" r={r} fill="none" stroke="var(--color-surface-hover)" strokeWidth="9" />
                <circle
                  cx="40" cy="40" r={r}
                  fill="none"
                  stroke="var(--color-brand)"
                  strokeWidth="9"
                  strokeLinecap="round"
                  strokeDasharray={`${filled} ${c}`}
                  transform="rotate(-90 40 40)"
                  style={{ transition: 'stroke-dasharray 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
              </svg>
              <div className="phs-donut-center">
                <span className="phs-donut-value">{data.donutCenterValue}</span>
                <span className="phs-donut-caption">{data.donutCenterCaption}</span>
              </div>
            </div>
            <div className="phs-summary-body">
              <span className="phs-summary-title">{data.title}</span>
              <span className="phs-summary-sub">{data.subtitle}</span>
            </div>
          </div>
          <div className="phs-summary-stats">
            {data.stats.map((s, i) => (
              <div key={i} className={`phs-summary-stat phs-summary-stat--${s.tone ?? 'neutral'}`}>
                <span className="phs-summary-stat-label">{s.label}</span>
                <span className="phs-summary-stat-value">{s.value}</span>
              </div>
            ))}
          </div>
          <div className="phs-tx-section-label">Transactions</div>
          <div className="phs-list">
            {data.rows.map((tx, i) => (
              <button
                key={`${tx.merchant}-${i}`}
                type="button"
                className="phs-row phs-row--button phs-row--stagger"
                style={{ animationDelay: `${40 + i * 40}ms` }}
                onClick={() => { onPickTx(tx); requestClose() }}
              >
                <span className={`phs-row-logo${tx.logo ? '' : ' phs-row-logo--mono'}`}>
                  {tx.logo ? <img src={tx.logo} alt="" /> : tx.initials}
                </span>
                <div className="phs-row-body">
                  <div className="phs-row-line1">
                    <span className="phs-row-title">{tx.merchant}</span>
                    <span className={`phs-row-amount${tx.direction === 'in' ? ' phs-row-amount--in' : ''}`}>{tx.amount}</span>
                  </div>
                  <div className={`phs-row-substatus phs-row-substatus--${tx.matched ? 'done' : 'todo'}`}>
                    {tx.matched ? (<><CheckMini /> Matched · {tx.time.replace('Today · ', '').replace('Yesterday · ', '')}</>) : (<>Awaiting match · 1 candidate</>)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}

/* Today's full 8 transactions (the 3 shown inline + 5 more) so "See all"
   has real depth. Kept inline rather than in a separate data module to
   match the file's "everything in one place" demo style. */
const TX_TODAY: TxData[] = [
  { merchant: 'Stripe payout', logo: '/logo-stripe.png', amount: '+€ 1 240', direction: 'in',  matched: true,  invoice: 'INV-2491',     category: 'Stripe payout',   time: 'Today · 09:41', account: 'EUR · Operating' },
  { merchant: 'Claude',        logo: '/logo-claude.png', amount: '−€ 20',    direction: 'out', matched: true,  invoice: 'AI · monthly', category: 'Software',        time: 'Today · 06:00', account: 'EUR · Operating' },
  { merchant: 'Notion',        logo: '/logo-notion.png', amount: '−€ 15',    direction: 'out', matched: false,                          category: 'Software',        time: 'Today · 04:12', account: 'EUR · Operating' },
  { merchant: 'AWS',                                       initials: 'AW',  amount: '−€ 142',   direction: 'out', matched: true,  invoice: 'AWS · monthly', category: 'Infrastructure',  time: 'Today · 03:30', account: 'EUR · Operating' },
  { merchant: 'Studio Aria d.o.o.',                        initials: 'SA',  amount: '+€ 745',   direction: 'in',  matched: true,  invoice: 'INV-2493',      category: 'Customer payment', time: 'Today · 11:08', account: 'EUR · Operating' },
  { merchant: 'Voli rentals',                              initials: 'VR',  amount: '−€ 350',   direction: 'out', matched: true,  invoice: 'INV-2487',      category: 'Rent',             time: 'Today · 10:00', account: 'EUR · Operating' },
  { merchant: 'GitHub',                                    initials: 'GH',  amount: '−€ 4',     direction: 'out', matched: true,  invoice: 'GH · monthly',  category: 'Software',         time: 'Today · 08:15', account: 'EUR · Operating' },
  { merchant: 'Stripe payout',  logo: '/logo-stripe.png', amount: '+€ 532',   direction: 'in',  matched: true,  invoice: 'INV-2492',      category: 'Stripe payout',   time: 'Today · 07:55', account: 'EUR · Operating' },
]

function PaymentsScreen() {
  const [selected, setSelected] = useState<SelectedTx | null>(null)
  const [openInvoice, setOpenInvoice] = useState<InvoiceData | null>(null)
  const [showScan, setShowScan] = useState(false)
  const [showSend, setShowSend] = useState(false)
  const [showRequest, setShowRequest] = useState(false)
  const [showConvert, setShowConvert] = useState(false)
  const [summary, setSummary] = useState<TxSummary | null>(null)
  const openTx = (tx: TxData) => setSelected(tx)

  return (
    <>
      <div className="phs-screen-head">
        <span className="phs-screen-title">Payments</span>
        <span className="phs-screen-count">7 days</span>
      </div>

      {/* Two large task tiles for the primary moves — Send + Request —
          immediately under the title (Plata hints §3, IMG_9353). Anything
          secondary (Convert, Scan) drops to the slim chip row below so
          there's a clear visual hierarchy between the action a user came
          here for and the helper utilities. */}
      <div className="phs-pay-tiles">
        <button type="button" className="phs-pay-tile phs-pay-tile--primary" onClick={() => setShowSend(true)}>
          <span className="phs-pay-tile-icon"><ArrowUpRight /></span>
          <span className="phs-pay-tile-label">Send</span>
          <span className="phs-pay-tile-sub">Pay supplier or staff</span>
        </button>
        <button type="button" className="phs-pay-tile" onClick={() => setShowRequest(true)}>
          <span className="phs-pay-tile-icon"><ArrowDownLeft /></span>
          <span className="phs-pay-tile-label">Request</span>
          <span className="phs-pay-tile-sub">Invoice a customer</span>
        </button>
      </div>
      <div className="phs-chips phs-chips--secondary">
        <button type="button" className="phs-chip" onClick={() => setShowConvert(true)}>
          <span className="phs-chip-icon"><SwapMini /></span>
          Convert
        </button>
        <button type="button" className="phs-chip" onClick={() => setShowScan(true)}>
          <span className="phs-chip-icon"><ScanMini /></span>
          Scan
        </button>
      </div>

      <button
        type="button"
        className="phs-pay-hero phs-pay-hero--button"
        onClick={() => setSummary({
          title: 'Reconciled today',
          subtitle: '12 of 14 transactions matched · 2 awaiting your review',
          donutLabel: 'Reconciliation rate',
          donutFraction: 12 / 14,
          donutCenterValue: '86%',
          donutCenterCaption: 'matched',
          stats: [
            { label: 'In', value: '+€ 1 985', tone: 'positive' },
            { label: 'Out', value: '−€ 412', tone: 'neutral' },
            { label: 'Matched', value: '12', tone: 'positive' },
            { label: 'Awaiting', value: '2', tone: 'warn' },
          ],
          rows: TX_TODAY,
        })}
      >
        <div className="phs-pay-hero-top">
          <span className="phs-pay-hero-label">Reconciled today</span>
          <span className="phs-pay-hero-fraction"><strong>12</strong> / 14</span>
        </div>
        <div className="phs-progress">
          <span className="phs-progress-fill" style={{ width: '86%' }} />
        </div>
        <div className="phs-pay-foot">
          <span className="phs-pay-foot-leg">
            In <span className="phs-pay-foot-value">+€ 1 985</span>
          </span>
          <span className="phs-pay-foot-leg phs-pay-foot-leg--out">
            Out <span className="phs-pay-foot-value">−€ 412</span>
          </span>
        </div>
      </button>

      {/* Day section — Wise-style flat list inside a single card with
          hairline dividers between rows. Match status sits as a single
          sub-line under the row title; the chartreuse check or ink
          arrow communicates state without an extra pill. */}
      <div className="phs-day-row">
        <span className="phs-day">Today · May 12</span>
        <button
          type="button"
          className="phs-day-link"
          onClick={() => setSummary({
            title: 'Today · May 12',
            subtitle: '8 transactions · net +€ 1 971',
            donutLabel: 'In vs. out',
            donutFraction: 1985 / (1985 + 412),
            donutCenterValue: '+€ 1 971',
            donutCenterCaption: 'net',
            stats: [
              { label: 'In', value: '+€ 1 985', tone: 'positive' },
              { label: 'Out', value: '−€ 412', tone: 'neutral' },
              { label: 'Transactions', value: '8' },
              { label: 'Awaiting match', value: '1', tone: 'warn' },
            ],
            rows: TX_TODAY,
          })}
        >
          See all 8
        </button>
      </div>
      <div className="phs-list">
        <button
          type="button"
          className="phs-row phs-row--button phs-row--stagger"
          style={{ animationDelay: '80ms' }}
          onClick={() => openTx({
            merchant: 'Stripe payout',
            logo: '/logo-stripe.png',
            amount: '+€ 1 240',
            direction: 'in',
            matched: true,
            invoice: 'INV-2491',
            category: 'Stripe payout',
            time: 'Today · 09:41',
            account: 'EUR · Operating',
          })}
        >
          <span className="phs-row-logo"><img src="/logo-stripe.png" alt="" /></span>
          <div className="phs-row-body">
            <div className="phs-row-line1">
              <span className="phs-row-title">Stripe payout</span>
              <span className="phs-row-amount phs-row-amount--in">+€ 1 240</span>
            </div>
            <div className="phs-row-substatus phs-row-substatus--done">
              <CheckMini /> Matched INV-2491 · 09:41
            </div>
          </div>
        </button>
        <button
          type="button"
          className="phs-row phs-row--button phs-row--stagger"
          style={{ animationDelay: '140ms' }}
          onClick={() => openTx({
            merchant: 'Claude',
            logo: '/logo-claude.png',
            amount: '−€ 20',
            direction: 'out',
            matched: true,
            invoice: 'AI · monthly',
            category: 'Software',
            time: 'Today · 06:00',
            account: 'EUR · Operating',
          })}
        >
          <span className="phs-row-logo"><img src="/logo-claude.png" alt="" /></span>
          <div className="phs-row-body">
            <div className="phs-row-line1">
              <span className="phs-row-title">Claude</span>
              <span className="phs-row-amount">−€ 20</span>
            </div>
            <div className="phs-row-substatus phs-row-substatus--done">
              <CheckMini /> Matched · AI subscription
            </div>
          </div>
        </button>
        <button
          type="button"
          className="phs-row phs-row--button phs-row--stagger"
          style={{ animationDelay: '200ms' }}
          onClick={() => openTx({
            merchant: 'Notion',
            logo: '/logo-notion.png',
            amount: '−€ 15',
            direction: 'out',
            matched: false,
            category: 'Software',
            time: 'Today · 04:12',
            account: 'EUR · Operating',
          })}
        >
          <span className="phs-row-logo"><img src="/logo-notion.png" alt="" /></span>
          <div className="phs-row-body">
            <div className="phs-row-line1">
              <span className="phs-row-title">Notion</span>
              <span className="phs-row-amount">−€ 15</span>
            </div>
            <div className="phs-row-substatus phs-row-substatus--todo">
              Awaiting match · 1 candidate
              <ArrowRight />
            </div>
          </div>
        </button>
      </div>

      <div className="phs-day-row">
        <span className="phs-day">Yesterday</span>
        <button type="button" className="phs-day-link">See all 6</button>
      </div>
      <div className="phs-list">
        <button
          type="button"
          className="phs-row phs-row--button phs-row--stagger"
          style={{ animationDelay: '280ms' }}
          onClick={() => openTx({
            merchant: 'Atelje Nikolić',
            initials: 'AN',
            amount: '+€ 745',
            direction: 'in',
            matched: true,
            invoice: 'INV-2487',
            category: 'Customer payment',
            time: 'Yesterday · 16:24',
            account: 'EUR · Operating',
          })}
        >
          <span className="phs-row-logo phs-row-logo--mono">AN</span>
          <div className="phs-row-body">
            <div className="phs-row-line1">
              <span className="phs-row-title">Atelje Nikolić</span>
              <span className="phs-row-amount phs-row-amount--in">+€ 745</span>
            </div>
            <div className="phs-row-substatus phs-row-substatus--done">
              <CheckMini /> Matched INV-2487
            </div>
          </div>
        </button>
      </div>

      {selected && (
        <TxDetailSheet
          tx={selected}
          onClose={() => setSelected(null)}
          onOpenInvoice={(ref) => { const inv = INVOICES[ref]; if (inv) setOpenInvoice(inv) }}
        />
      )}
      {openInvoice && <InvoiceDetailSheet invoice={openInvoice} onClose={() => setOpenInvoice(null)} />}
      {summary && <TxSummarySheet data={summary} onClose={() => setSummary(null)} onPickTx={(tx) => setSelected(tx)} />}
      {showScan && <ScanOverlay onClose={() => setShowScan(false)} />}
      {showSend && <PaymentSendSheet onClose={() => setShowSend(false)} />}
      {showRequest && <RequestPaymentSheet onClose={() => setShowRequest(false)} />}
      {showConvert && <ConvertSheet onClose={() => setShowConvert(false)} />}
    </>
  )
}

/* ── More sub-view icons (Agent / Reports / Tax / Docs / Settings / Help) */
function IconAgent() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M 9 1 L 4 8.5 H 7 L 6 14.5 L 12 6.5 H 8.5 z" />
    </svg>
  )
}
function IconReports() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 3 13 V 6.5 M 7 13 V 4 M 11 13 V 8 M 2 13 H 14" />
    </svg>
  )
}
function IconTax() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 4 2 H 10 L 12 4 V 14 H 4 z" />
      <path d="M 6 6 L 10 10 M 10 6 L 6 10" />
    </svg>
  )
}
function IconDocs() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 4 2 H 9 L 12 5 V 14 H 4 z" />
      <path d="M 9 2 V 5 H 12" />
      <path d="M 6 8 H 10 M 6 11 H 10" />
    </svg>
  )
}
function IconGear() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M 8 5.2 a 2.8 2.8 0 1 0 0 5.6 a 2.8 2.8 0 0 0 0 -5.6 z m 0 1.4 a 1.4 1.4 0 1 1 0 2.8 a 1.4 1.4 0 0 1 0 -2.8 z" />
      <path d="M 7 1 h 2 l 0.4 1.8 l 1.5 0.6 l 1.6 -0.9 l 1.4 1.4 l -0.9 1.6 l 0.6 1.5 L 15 7 v 2 l -1.8 0.4 l -0.6 1.5 l 0.9 1.6 l -1.4 1.4 l -1.6 -0.9 l -1.5 0.6 L 9 15 H 7 l -0.4 -1.8 l -1.5 -0.6 l -1.6 0.9 l -1.4 -1.4 l 0.9 -1.6 l -0.6 -1.5 L 1 9 V 7 l 1.8 -0.4 l 0.6 -1.5 l -0.9 -1.6 l 1.4 -1.4 l 1.6 0.9 l 1.5 -0.6 z" fillRule="evenodd" clipRule="evenodd" />
    </svg>
  )
}
function IconHelp() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
      <circle cx="8" cy="8" r="6" />
      <path d="M 6.2 6.2 a 1.8 1.8 0 1 1 2.8 1.5 c -0.6 0.4 -1 0.7 -1 1.4 M 8 11.3 v 0.1" />
    </svg>
  )
}
/* Sign-out glyph: door + outward arrow. Reads as "leave" rather than the
   ambiguous power-off icon (which also implies "turn off your phone").
   Lives only in the destructive row at the bottom of More. */
function IconSignOut() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 9 3 H 4 a 1 1 0 0 0 -1 1 v 8 a 1 1 0 0 0 1 1 h 5" />
      <path d="M 11 5.5 L 13.5 8 L 11 10.5 M 6.5 8 H 13.5" />
    </svg>
  )
}
/* Palette glyph: dot-cluster on a rounded base — reads as "choose a
   colour" without resorting to a stripe of swatches (which would clash
   visually with the swatch tiles inside Appearance view). */
function IconPalette() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M 8 2 a 6 6 0 1 0 1.6 11.8 a 1 1 0 0 0 0.3 -1.7 a 1.3 1.3 0 0 1 0.7 -2.3 H 12 a 2 2 0 0 0 2 -2 A 6 6 0 0 0 8 2 z" />
      <circle cx="5.5" cy="7" r="0.8" fill="currentColor" stroke="none" />
      <circle cx="8" cy="5" r="0.8" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="6.5" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  )
}
/* Mini-swatch shown in the Appearance row meta slot — instantly reads
   the current pick without opening the picker. Uses the same gradient
   tokens as .phs-home-hero-bg so the badge matches what the user will
   see on Home. */
function PhoneThemeBadge() {
  const { theme } = useContext(PhoneThemeContext)
  return <span className="phs-theme-badge" data-phone-theme={theme} aria-hidden />
}
function ChevronLeft() {
  return (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M 9 2 L 4 7 L 9 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

type MoreView = 'menu' | 'agent' | 'reports' | 'tax' | 'docs' | 'settings' | 'appearance' | 'help'

function MoreSubHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="phs-subhead">
      <button type="button" className="phs-subhead-back" onClick={onBack} aria-label="Back">
        <ChevronLeft />
      </button>
      <span className="phs-subhead-title">{title}</span>
    </div>
  )
}

function MoreMenu({ onPick, onProfile }: { onPick: (v: MoreView) => void; onProfile: () => void }) {
  return (
    <>
      <div className="phs-screen-head">
        <span className="phs-screen-title">More</span>
      </div>

      <button type="button" className="phs-profile phs-profile--button" onClick={onProfile}>
        <span className="phs-profile-avatar">
          <img src="/marko.jpg" alt="" />
        </span>
        <span className="phs-profile-body">
          <span className="phs-profile-name">Marko Petrović</span>
          <span className="phs-profile-org">acc.tixo · Operating EUR</span>
        </span>
        <Chevron />
      </button>

      {/* All More icons share a single neutral slate-gradient surface +
          white line glyph — distinguished only by the glyph shape (bank-ux
          §13: simple line glyphs inside the same rounded square). The "3
          new" meta-badge on Agent gets the brand-soft pill so the only
          *live* row in the screen still earns a tiny accent moment. */}
      <span className="phs-group-label">Sections</span>
      <div className="phs-group">
        <button type="button" className="phs-item" onClick={() => onPick('agent')}>
          <span className="phs-item-icon phs-item-icon--slate"><IconAgent /></span>
          <span className="phs-item-label">Agent activity</span>
          <span className="phs-item-meta phs-item-meta--brand">3 new</span>
          <Chevron />
        </button>
        <button type="button" className="phs-item" onClick={() => onPick('reports')}>
          <span className="phs-item-icon phs-item-icon--slate"><IconReports /></span>
          <span className="phs-item-label">Reports</span>
          <Chevron />
        </button>
        <button type="button" className="phs-item" onClick={() => onPick('tax')}>
          <span className="phs-item-icon phs-item-icon--slate"><IconTax /></span>
          <span className="phs-item-label">Tax</span>
          <span className="phs-item-meta phs-item-meta--tag">PDV Q1</span>
          <Chevron />
        </button>
        <button type="button" className="phs-item" onClick={() => onPick('docs')}>
          <span className="phs-item-icon phs-item-icon--slate"><IconDocs /></span>
          <span className="phs-item-label">Documents</span>
          <Chevron />
        </button>
      </div>

      <span className="phs-group-label">Account</span>
      <div className="phs-group">
        <button type="button" className="phs-item" onClick={() => onPick('settings')}>
          <span className="phs-item-icon phs-item-icon--slate"><IconGear /></span>
          <span className="phs-item-label">Settings</span>
          <Chevron />
        </button>
        <button type="button" className="phs-item" onClick={() => onPick('appearance')}>
          <span className="phs-item-icon phs-item-icon--slate"><IconPalette /></span>
          <span className="phs-item-label">Appearance</span>
          <span className="phs-item-meta phs-item-meta--theme"><PhoneThemeBadge /></span>
          <Chevron />
        </button>
        <button type="button" className="phs-item" onClick={() => onPick('help')}>
          <span className="phs-item-icon phs-item-icon--slate"><IconHelp /></span>
          <span className="phs-item-label">Help &amp; support</span>
          <Chevron />
        </button>
      </div>

      <div className="phs-group phs-group--danger">
        <button type="button" className="phs-item phs-item--danger">
          <span className="phs-item-icon phs-item-icon--danger"><IconSignOut /></span>
          <span className="phs-item-label">Sign out</span>
        </button>
      </div>

      {/* Quiet footer — pushes app version + build out of the screen head
          where it was competing with the page title. Reads as identity
          metadata, not a UI control. */}
      <div className="phs-app-footer">
        <span className="phs-app-footer-mark" aria-hidden>
          <span /><span /><span />
        </span>
        acc.tixo · v 1.4.2 · build 245
      </div>
    </>
  )
}

function MoreAgentView({ onBack }: { onBack: () => void }) {
  return (
    <>
      <MoreSubHeader title="Agent activity" onBack={onBack} />
      <AgentRunCard />
      <PhoneSceneReconciled />
      <PhoneSceneAging />
    </>
  )
}

function MoreSettingsView({ onBack }: { onBack: () => void }) {
  return (
    <>
      <MoreSubHeader title="Settings" onBack={onBack} />

      <span className="phs-group-label">Account</span>
      <div className="phs-group">
        <div className="phs-item">
          <span className="phs-item-icon phs-item-icon--slate"><IconUser /></span>
          <span className="phs-item-label">Profile details</span>
          <Chevron />
        </div>
        <div className="phs-item">
          <span className="phs-item-icon phs-item-icon--slate"><IconDollar /></span>
          <span className="phs-item-label">Payout currency</span>
          <span className="phs-item-meta">EUR</span>
          <Chevron />
        </div>
        <div className="phs-item">
          <span className="phs-item-icon phs-item-icon--slate"><IconGlobe /></span>
          <span className="phs-item-label">Language</span>
          <span className="phs-item-meta">English</span>
          <Chevron />
        </div>
      </div>

      <span className="phs-group-label">Security</span>
      <div className="phs-group">
        <div className="phs-item">
          <span className="phs-item-icon phs-item-icon--slate"><IconFingerprint /></span>
          <span className="phs-item-label">Face ID</span>
          <span className="phs-toggle phs-toggle--on" />
        </div>
        <div className="phs-item">
          <span className="phs-item-icon phs-item-icon--slate"><IconKey /></span>
          <span className="phs-item-label">Passkey</span>
          <span className="phs-toggle phs-toggle--on" />
        </div>
        <div className="phs-item">
          <span className="phs-item-icon phs-item-icon--slate"><IconBell /></span>
          <span className="phs-item-label">Approval alerts</span>
          <span className="phs-toggle phs-toggle--on" />
        </div>
      </div>
    </>
  )
}

/* Appearance — two independent axes:
   • Theme    = palette/styling, applied to the whole app (light/dark/…)
   • Wallpaper = optional photo behind Home upper-half only
   When a wallpaper is picked, two more knobs appear: glass blur (the
   wallet card's backdrop-filter) and corner radius (the hero card's
   border-radius). Both write to per-instance CSS variables so the user
   can see live changes as they drag. */
function MoreAppearanceView({ onBack }: { onBack: () => void }) {
  const { theme, setTheme, wallpaper, setWallpaper, glassBlur, setGlassBlur, heroRadius, setHeroRadius, wallpaperBlur, setWallpaperBlur } = useContext(PhoneThemeContext)
  return (
    <>
      <MoreSubHeader title="Appearance" onBack={onBack} />

      <span className="phs-group-label">Theme</span>
      <div className="phs-theme-grid">
        {PHONE_THEMES.map(opt => (
          <button
            key={opt.id}
            type="button"
            className={`phs-theme-tile${theme === opt.id ? ' phs-theme-tile--active' : ''}`}
            onClick={() => setTheme(opt.id)}
            aria-pressed={theme === opt.id}
          >
            <div className="phs-theme-preview" data-phone-theme={opt.id} aria-hidden>
              <div className="phs-theme-preview-bg" />
              <div className="phs-theme-preview-greet">
                <span className="phs-theme-preview-greet-name" />
                <span className="phs-theme-preview-greet-avatar" />
              </div>
              <div className="phs-theme-preview-wallet">
                <span />
                <span />
                <span />
              </div>
            </div>
            <div className="phs-theme-tile-label">
              <span className="phs-theme-tile-name">{opt.label}</span>
              <span className="phs-theme-tile-sub">{opt.sub}</span>
            </div>
            {theme === opt.id && (
              <span className="phs-theme-tile-check" aria-hidden>
                <svg viewBox="0 0 14 14" fill="none">
                  <path d="M 3 7.2 L 5.8 9.8 L 11 4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
          </button>
        ))}
      </div>

      <span className="phs-group-label">Wallpaper</span>
      <div className="phs-theme-grid">
        {PHONE_WALLPAPERS.map(opt => (
          <button
            key={opt.id}
            type="button"
            className={`phs-theme-tile${wallpaper === opt.id ? ' phs-theme-tile--active' : ''}`}
            onClick={() => setWallpaper(opt.id)}
            aria-pressed={wallpaper === opt.id}
          >
            <div className="phs-theme-preview" data-phone-wallpaper={opt.id} aria-hidden>
              <div className="phs-theme-preview-bg" />
              <div className="phs-theme-preview-greet">
                <span className="phs-theme-preview-greet-name" />
                <span className="phs-theme-preview-greet-avatar" />
              </div>
              <div className="phs-theme-preview-wallet">
                <span />
                <span />
                <span />
              </div>
            </div>
            <div className="phs-theme-tile-label">
              <span className="phs-theme-tile-name">{opt.label}</span>
              <span className="phs-theme-tile-sub">{opt.sub}</span>
            </div>
            {wallpaper === opt.id && (
              <span className="phs-theme-tile-check" aria-hidden>
                <svg viewBox="0 0 14 14" fill="none">
                  <path d="M 3 7.2 L 5.8 9.8 L 11 4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
          </button>
        ))}
      </div>

      {wallpaper !== 'none' && (
        <>
          <span className="phs-group-label">Wallpaper tuning</span>
          <div className="phs-group">
            <label className="phs-item phs-slider-row">
              <span className="phs-item-label">Wallpaper blur</span>
              <span className="phs-item-meta">{wallpaperBlur}px</span>
              <input
                type="range"
                className="phs-slider"
                min={0}
                max={24}
                step={1}
                value={wallpaperBlur}
                onChange={e => setWallpaperBlur(Number(e.target.value))}
              />
            </label>
            <label className="phs-item phs-slider-row">
              <span className="phs-item-label">Glass blur</span>
              <span className="phs-item-meta">{glassBlur}px</span>
              <input
                type="range"
                className="phs-slider"
                min={0}
                max={32}
                step={1}
                value={glassBlur}
                onChange={e => setGlassBlur(Number(e.target.value))}
              />
            </label>
            <label className="phs-item phs-slider-row">
              <span className="phs-item-label">Corner radius</span>
              <span className="phs-item-meta">{heroRadius}px</span>
              <input
                type="range"
                className="phs-slider"
                min={0}
                max={32}
                step={1}
                value={heroRadius}
                onChange={e => setHeroRadius(Number(e.target.value))}
              />
            </label>
          </div>
        </>
      )}

      <p className="phs-theme-hint">
        Theme paints the whole app. Wallpaper sits behind the Home
        greeting + wallet only, with blur and corner controls for the
        glass card on top.
      </p>
    </>
  )
}

/* Placeholder with a concrete next-action (bank-ux §10): banks never
   show a passive empty card with a description — they always surface a
   single action the user can take right now (Add a Category, Upload,
   Pin a Report). The cta string here lands as the visible button. */
function MorePlaceholderView({
  title,
  onBack,
  body,
  cta,
}: {
  title: string
  onBack: () => void
  body: string
  cta: string
}) {
  return (
    <>
      <MoreSubHeader title={title} onBack={onBack} />
      <div className="phs-placeholder">
        <div className="phs-placeholder-mark" aria-hidden />
        <span className="phs-placeholder-title">{title}</span>
        <span className="phs-placeholder-body">{body}</span>
        <button type="button" className="phs-placeholder-cta">{cta}</button>
      </div>
    </>
  )
}

function MoreScreen() {
  const [view, setView] = useState<MoreView>('menu')
  const [profileOpen, setProfileOpen] = useState(false)
  const back = () => setView('menu')

  if (view === 'agent')      return <MoreAgentView onBack={back} />
  if (view === 'settings')   return <MoreSettingsView onBack={back} />
  if (view === 'appearance') return <MoreAppearanceView onBack={back} />
  if (view === 'reports')    return <MorePlaceholderView title="Reports" onBack={back} body="P&L, cash flow, runway projections — pinnable to home." cta="Pin first report" />
  if (view === 'tax')        return <MorePlaceholderView title="Tax" onBack={back} body="PDV Q1 is ready · auto-submit available." cta="Review PDV Q1" />
  if (view === 'docs')       return <MorePlaceholderView title="Documents" onBack={back} body="Contracts, invoices, and signed PDFs in one place." cta="Upload a document" />
  if (view === 'help')       return <MorePlaceholderView title="Help & support" onBack={back} body="Talk to the team or search the docs." cta="Open chat" />
  return (
    <>
      <MoreMenu onPick={setView} onProfile={() => setProfileOpen(true)} />
      {profileOpen && <ProfileSheet onClose={() => setProfileOpen(false)} />}
    </>
  )
}

/* ── Onboarding overlay — first-run welcome card stack ─────────────────
   Appears as a soft glass card over the phone screen on first visit
   (and re-openable via the More tab → Help in a later iter). Three
   slides walk through: forward bills, agent matches, signed contracts.
   Same iOS sheet-style chrome as the other overlays so the user keeps
   their muscle memory: tap outside → close, swipe down → close, X
   button → close. */
const ONBOARD_SLIDES = [
  {
    eyebrow: 'Welcome',
    title: 'A bank that does the books',
    body: 'acc.tixo runs your accounting alongside your money — the agent reconciles, matches, and prepares filings as you transact.',
    art: 'wave',
  },
  {
    eyebrow: 'Step 1',
    title: 'Forward a bill',
    body: 'Drop any invoice — email, screenshot, photo — into the Scan tile. The agent extracts recipient, amount, and reference.',
    art: 'scan',
  },
  {
    eyebrow: 'Step 2',
    title: 'Tap to confirm',
    body: 'Every payment, match, and contract is one tap away. Slide to send, sign in-line, sheets slide down when you’re done.',
    art: 'check',
  },
] as const

function OnboardArt({ kind }: { kind: typeof ONBOARD_SLIDES[number]['art'] }) {
  if (kind === 'wave') {
    return (
      <div className="phs-onb-art phs-onb-art--wave">
        <svg viewBox="0 0 120 64" fill="none" aria-hidden>
          <path className="phs-onb-wave-1" d="M 4 38 Q 24 22 44 38 T 84 38 T 124 38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path className="phs-onb-wave-2" d="M 4 46 Q 24 30 44 46 T 84 46 T 124 46" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
          <circle cx="60" cy="32" r="14" fill="currentColor" opacity="0.12" />
          <circle cx="60" cy="32" r="6" fill="currentColor" />
        </svg>
      </div>
    )
  }
  if (kind === 'scan') {
    return (
      <div className="phs-onb-art phs-onb-art--scan">
        <svg viewBox="0 0 96 64" fill="none" aria-hidden>
          <rect x="6" y="8" width="48" height="48" rx="6" fill="currentColor" opacity="0.12" />
          <rect x="12" y="14" width="36" height="3" rx="1" fill="currentColor" opacity="0.5" />
          <rect x="12" y="22" width="28" height="3" rx="1" fill="currentColor" opacity="0.35" />
          <rect x="12" y="30" width="32" height="3" rx="1" fill="currentColor" opacity="0.35" />
          <rect x="12" y="44" width="20" height="6" rx="1.5" fill="currentColor" />
          <path className="phs-onb-scan-beam" d="M 4 32 H 56" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 3" />
          <g transform="translate(56 8)">
            <path d="M 4 8 V 4 a 2 2 0 0 1 2 -2 H 10" stroke="currentColor" strokeWidth="1.6" />
            <path d="M 24 2 H 30 a 2 2 0 0 1 2 2 V 8" stroke="currentColor" strokeWidth="1.6" />
            <path d="M 32 36 V 40 a 2 2 0 0 1 -2 2 H 24" stroke="currentColor" strokeWidth="1.6" />
            <path d="M 10 42 H 6 a 2 2 0 0 1 -2 -2 V 36" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="18" cy="22" r="6" stroke="currentColor" strokeWidth="1.6" />
          </g>
        </svg>
      </div>
    )
  }
  return (
    <div className="phs-onb-art phs-onb-art--check">
      <svg viewBox="0 0 80 80" fill="none" aria-hidden>
        <circle cx="40" cy="40" r="32" fill="currentColor" opacity="0.12" />
        <circle className="phs-onb-check-ring" cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="2" />
        <path className="phs-onb-check-tick" d="M 24 41 L 35 52 L 56 30" stroke="currentColor" strokeWidth="4.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

function OnboardingOverlay({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState(0)
  const { closing, requestClose } = useSheetClose(onClose)
  const sheetRef = useRef<HTMLDivElement>(null)
  useSheetSwipe(sheetRef, requestClose)
  const slide = ONBOARD_SLIDES[step]
  const isLast = step === ONBOARD_SLIDES.length - 1
  return (
    <SheetPortal>
      <div className={`phs-tx-backdrop${closing ? ' phs-tx-backdrop--closing' : ''}`} onClick={requestClose} aria-hidden />
      <div ref={sheetRef} className={`phs-tx-sheet phs-onb-sheet${closing ? ' phs-tx-sheet--closing' : ''}`}>
        <div className="phs-tx-grip" aria-hidden />
        <button type="button" className="phs-onb-skip" onClick={requestClose}>Skip</button>
        <div className="phs-onb-body" key={step}>
          <span className="phs-onb-eyebrow">{slide.eyebrow}</span>
          <OnboardArt kind={slide.art} />
          <h3 className="phs-onb-title">{slide.title}</h3>
          <p className="phs-onb-body-text">{slide.body}</p>
        </div>
        <div className="phs-onb-foot">
          <div className="phs-onb-dots" aria-hidden>
            {ONBOARD_SLIDES.map((_, i) => (
              <span key={i} className={`phs-onb-dot${i === step ? ' phs-onb-dot--active' : ''}`} />
            ))}
          </div>
          <button
            type="button"
            className="phs-tx-action phs-tx-action--primary phs-onb-cta"
            onClick={() => (isLast ? requestClose() : setStep(s => s + 1))}
          >
            {isLast ? 'Start' : 'Next'}
          </button>
        </div>
      </div>
    </SheetPortal>
  )
}

/** Interactive 4-tab phone mockup. Tabs are real buttons that swap the
 *  visible screen with a fade+rise animation. Designed to be lift-and-
 *  shifted into an Expo / SwiftUI app — the same tab graph, same content
 *  hierarchy, same visual rhythm. */
export function PhoneApp({ initialTab = 'payments' }: { initialTab?: ActiveTab } = {}) {
  const [tab, setTab] = useState<ActiveTab>(initialTab)
  /* Onboarding shows once per browser session — sessionStorage flag set
     on dismiss. Tapping the avatar in More re-arms it for the next mount
     so the user can replay the tutorial without clearing storage. */
  const [showOnboard, setShowOnboard] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.sessionStorage.getItem('phs-onboard-seen') !== '1'
  })
  const dismissOnboard = () => {
    if (typeof window !== 'undefined') window.sessionStorage.setItem('phs-onboard-seen', '1')
    setShowOnboard(false)
  }
  const [theme, setThemeState] = useState<PhoneTheme>(() => {
    if (typeof window === 'undefined') return 'classic'
    const stored = window.sessionStorage.getItem('phs-app-theme') as PhoneTheme | null
    return stored && PHONE_THEMES.some(t => t.id === stored) ? stored : 'classic'
  })
  const setTheme = (t: PhoneTheme) => {
    if (typeof window !== 'undefined') window.sessionStorage.setItem('phs-app-theme', t)
    setThemeState(t)
  }
  const [wallpaper, setWallpaperState] = useState<PhoneWallpaper>(() => {
    if (typeof window === 'undefined') return 'orchard'
    const stored = window.sessionStorage.getItem('phs-app-wallpaper') as PhoneWallpaper | null
    return stored && PHONE_WALLPAPERS.some(w => w.id === stored) ? stored : 'orchard'
  })
  const setWallpaper = (w: PhoneWallpaper) => {
    if (typeof window !== 'undefined') window.sessionStorage.setItem('phs-app-wallpaper', w)
    setWallpaperState(w)
  }
  const [glassBlur, setGlassBlurState] = useState<number>(() => {
    if (typeof window === 'undefined') return 18
    const n = Number(window.sessionStorage.getItem('phs-glass-blur'))
    return Number.isFinite(n) && n >= 0 && n <= 40 ? n : 18
  })
  const setGlassBlur = (n: number) => {
    if (typeof window !== 'undefined') window.sessionStorage.setItem('phs-glass-blur', String(n))
    setGlassBlurState(n)
  }
  const [heroRadius, setHeroRadiusState] = useState<number>(() => {
    if (typeof window === 'undefined') return 22
    const n = Number(window.sessionStorage.getItem('phs-hero-radius'))
    return Number.isFinite(n) && n >= 0 && n <= 32 ? n : 22
  })
  const setHeroRadius = (n: number) => {
    if (typeof window !== 'undefined') window.sessionStorage.setItem('phs-hero-radius', String(n))
    setHeroRadiusState(n)
  }
  const [wallpaperBlur, setWallpaperBlurState] = useState<number>(() => {
    if (typeof window === 'undefined') return 0
    const n = Number(window.sessionStorage.getItem('phs-wallpaper-blur'))
    return Number.isFinite(n) && n >= 0 && n <= 24 ? n : 0
  })
  const setWallpaperBlur = (n: number) => {
    if (typeof window !== 'undefined') window.sessionStorage.setItem('phs-wallpaper-blur', String(n))
    setWallpaperBlurState(n)
  }
  return (
    <PhoneThemeContext.Provider value={{ theme, setTheme, wallpaper, setWallpaper, glassBlur, setGlassBlur, heroRadius, setHeroRadius, wallpaperBlur, setWallpaperBlur }}>
      <PhoneFrame active={tab} onTabChange={setTab} phoneTheme={theme} wallpaper={wallpaper} wallpaperBlur={wallpaperBlur}>
        {tab === 'home' && <HomeScreen />}
        {tab === 'contracts' && <ContractsScreen />}
        {tab === 'payments' && <PaymentsScreen />}
        {tab === 'more' && <MoreScreen />}
        {showOnboard && <OnboardingOverlay onClose={dismissOnboard} />}
      </PhoneFrame>
    </PhoneThemeContext.Provider>
  )
}

/** Legacy named exports — each still works, just renders the PhoneApp
 *  pre-set to that tab. Useful for consumers that want a single static
 *  screen embedded in marketing copy. */
export function PhoneScreenContracts() { return <PhoneApp initialTab="contracts" /> }
export function PhoneScreenPayments()  { return <PhoneApp initialTab="payments"  /> }
export function PhoneScreenSettings()  { return <PhoneApp initialTab="more"      /> }
